import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import api from "../../../shared/api/apiClient";
import { crmSettingsApi } from "../../settings/api/crmSettings";
import "bootstrap/dist/css/bootstrap.min.css";
import "./CategoryManagerPage.css";

const WAREHOUSE_PRESET_KEYS = new Set([
  "warehouse",
  "wood",
  "wood_warehouse",
  "timber",
  "lumber",
  "manufacturing",
  "warehouse_shop",
  "wood_sales",
  "woodworking",
  "wood_sales_woodworking",
  "wood_sales_and_woodworking",
  "wood_sales/woodworking",
]);

const WAREHOUSE_CATEGORY_HINTS = [
  "wood",
  "timber",
  "lumber",
  "oak",
  "pine",
  "warehouse",
  "stock",
  "древес",
  "дерево",
  "лес",
  "брус",
  "доск",
  "доски",
  "доска",
  "мебель",
  "wooden",
  "board",
  "beam",
  "plank",
];

const NON_WAREHOUSE_CATEGORY_HINTS = [
  "delivery",
  "shipping",
  "service",
  "services",
  "install",
  "installation",
  "repair",
  "custom",
  "individual",
  "event",
  "consulting",
  "доставка",
  "услуг",
  "услуга",
  "сервис",
  "ремонт",
  "монтаж",
  "индивидуаль",
  "заказ",
];

function normalizePresetKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function getPresetKeyFromSettings(settings) {
  const settingsJson =
    settings?.settings_json && typeof settings.settings_json === "object"
      ? settings.settings_json
      : {};

  const businessPreset = settingsJson.business_preset;
  const industry = settingsJson.industry;

  if (typeof businessPreset === "string") return normalizePresetKey(businessPreset);
  if (businessPreset?.key) return normalizePresetKey(businessPreset.key);
  if (businessPreset?.preset_key) return normalizePresetKey(businessPreset.preset_key);

  if (typeof industry === "string") return normalizePresetKey(industry);
  if (industry?.key) return normalizePresetKey(industry.key);

  return "";
}


function presetLooksLikeWarehouse(value) {
  const key = normalizePresetKey(value);

  if (WAREHOUSE_PRESET_KEYS.has(key)) return true;

  return (
    key.includes("wood") ||
    key.includes("timber") ||
    key.includes("lumber") ||
    key.includes("warehouse") ||
    key.includes("woodworking") ||
    key.includes("wood_sales")
  );
}

function toLowerText(value) {
  return String(value || "").toLowerCase();
}



function CategorySearchSelect({
  value,
  categories = [],
  getCategoryName,
  onChange,
  placeholder,
  searchPlaceholder,
  disabled = false,
}) {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const selectedCategory = useMemo(
    () => categories.find((category) => String(category.id) === String(value || "")) || null,
    [categories, value]
  );

  const filteredCategories = useMemo(() => {
    const normalizedQuery = String(query || "").trim().toLowerCase();
    if (!normalizedQuery) return categories;

    return categories.filter((category) => {
      const label = String(getCategoryName(category) || "").toLowerCase();
      const rawName = String(category?.name || "").toLowerCase();
      const id = String(category?.id || "").toLowerCase();
      return label.includes(normalizedQuery) || rawName.includes(normalizedQuery) || id.includes(normalizedQuery);
    });
  }, [categories, getCategoryName, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!open || !rootRef.current || rootRef.current.contains(event.target)) return;
      setOpen(false);
      setQuery("");
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open]);

  const openDropdown = () => {
    if (disabled) return;
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const pickCategory = (categoryId) => {
    onChange?.({ target: { value: categoryId ? String(categoryId) : "" } });
    setOpen(false);
    setQuery("");
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
      setQuery("");
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) => Math.min(filteredCategories.length - 1, current + 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) => Math.max(0, current - 1));
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const category = filteredCategories[activeIndex];
      if (category) pickCategory(category.id);
    }
  };

  return (
    <div className={`cmp-category-search-select ${open ? "is-open" : ""}`} ref={rootRef}>
      <button
        type="button"
        className="cmp-category-search-trigger"
        onClick={() => (open ? setOpen(false) : openDropdown())}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selectedCategory ? "" : "is-placeholder"}>
          {selectedCategory ? getCategoryName(selectedCategory) : placeholder}
        </span>
        <i className={`lni ${open ? "lni-chevron-up" : "lni-chevron-down"}`}></i>
      </button>

      {open && (
        <div className="cmp-category-search-menu">
          <div className="cmp-category-search-box">
            <i className="lni lni-search-alt"></i>
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder={searchPlaceholder}
              autoComplete="off"
            />
            {query ? (
              <button type="button" className="cmp-category-search-clear" onClick={() => setQuery("")} aria-label="Clear search">×</button>
            ) : null}
          </div>

          <div className="cmp-category-search-options" role="listbox">
            {filteredCategories.length ? (
              filteredCategories.map((category, index) => {
                const selected = String(category.id) === String(value || "");
                return (
                  <button
                    type="button"
                    key={category.id}
                    role="option"
                    aria-selected={selected}
                    className={`cmp-category-search-option ${selected ? "is-selected" : ""} ${index === activeIndex ? "is-active" : ""}`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => pickCategory(category.id)}
                  >
                    <span className="cmp-category-search-option-name">{getCategoryName(category)}</span>
                    {selected ? <i className="lni lni-checkmark"></i> : null}
                  </button>
                );
              })
            ) : (
              <div className="cmp-category-search-empty">No matching categories</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function flattenCatalogTree(nodes = [], depth = 0, result = []) {
  (Array.isArray(nodes) ? nodes : []).forEach((node) => {
    result.push({ ...node, depth });
    flattenCatalogTree(node.children || [], depth + 1, result);
  });
  return result;
}

const makeEmptyShopContent = () => ({
  shop_sku: "",
  shop_enabled: true,
  shop_short_description_ru: "",
  shop_short_description_en: "",
  shop_short_description_et: "",
  shop_description_ru: "",
  shop_description_en: "",
  shop_description_et: "",
  shop_delivery_ru: "",
  shop_delivery_en: "",
  shop_delivery_et: "",
  shop_payment_ru: "",
  shop_payment_en: "",
  shop_payment_et: "",
  shop_video_url: "",
  shop_faq_json: "[]",
  shop_seo_title_ru: "",
  shop_seo_title_en: "",
  shop_seo_title_et: "",
  shop_seo_description_ru: "",
  shop_seo_description_en: "",
  shop_seo_description_et: "",
});

function CategoryManagerPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tt = (key, fallback) => t(key, { defaultValue: fallback });

  const translatePresetValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "";
    }

    const key = String(value);
    return t(key, { defaultValue: key });
  };

  const getCategoryName = (category) => translatePresetValue(category?.name);
  const getSubcategoryName = (subcategory) => translatePresetValue(subcategory?.name);

  const [categories, setCategories] = useState([]);
  const [catalogTree, setCatalogTree] = useState([]);
  const [catalogDraft, setCatalogDraft] = useState({ name: "", parent_id: "" });
  const [catalogBusy, setCatalogBusy] = useState(false);
  const [catalogImportFile, setCatalogImportFile] = useState(null);
  const [catalogImportMode, setCatalogImportMode] = useState("merge");
  const [catalogImportBusy, setCatalogImportBusy] = useState(false);
  const [catalogSyncBusy, setCatalogSyncBusy] = useState(false);
  const [subcategories, setSubcategories] = useState([]);
  const [subcategoryFields, setSubcategoryFields] = useState([]);
  const [fieldManagerCategory, setFieldManagerCategory] = useState(null);
  const [newCustomField, setNewCustomField] = useState({
    key: "",
    label: "",
    data_type: "text",
    is_required: false,
    is_visible_in_table: true,
  });

  const [newCategory, setNewCategory] = useState("");
  const [newCategoryCatalogId, setNewCategoryCatalogId] = useState("");
  const [newSubcategory, setNewSubcategory] = useState({
    name: "",
    count: "",
    length: "",
    width: "",
    height: "",
    price_per_piece: "",
    categoryId: "",
    catalogCategoryId: "",
    custom_field_values: {},
  });

  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [updatedCategoryName, setUpdatedCategoryName] = useState("");
  const [updatedSubcategoryName, setUpdatedSubcategoryName] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isCreatingSubcategory, setIsCreatingSubcategory] = useState(false);
  const [photoManagerSubcategory, setPhotoManagerSubcategory] = useState(null);
  const [productPhotos, setProductPhotos] = useState([]);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);
  const [isSavingPhotos, setIsSavingPhotos] = useState(false);
  const [photoUploadQueue, setPhotoUploadQueue] = useState([]);
  const [renamingProductPhotoId, setRenamingProductPhotoId] = useState(null);
  const [downloadingProductPhotoId, setDownloadingProductPhotoId] = useState(null);
  const [contentManagerSubcategory, setContentManagerSubcategory] = useState(null);
  const [contentLanguage, setContentLanguage] = useState("ru");
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [isSavingContent, setIsSavingContent] = useState(false);
  const [shopContent, setShopContent] = useState(makeEmptyShopContent);
  const [retailShopEnabled, setRetailShopEnabled] = useState(false);
  const [createShopModalOpen, setCreateShopModalOpen] = useState(false);
  const [createShopAdvancedOpen, setCreateShopAdvancedOpen] = useState(false);
  const [shopTemplates, setShopTemplates] = useState({ delivery: [], payment: [], faq: [] });
  const [selectedShopTemplate, setSelectedShopTemplate] = useState({ delivery: "", payment: "", faq: "" });
  const [shopTemplateBusy, setShopTemplateBusy] = useState("");
  const [shopTranslationBusy, setShopTranslationBusy] = useState("");
  const [catalogLanguage, setCatalogLanguage] = useState("ru");
  const [catalogTranslations, setCatalogTranslations] = useState({});
  const [catalogTranslationBusy, setCatalogTranslationBusy] = useState("");
  const [catalogMenuSearch, setCatalogMenuSearch] = useState("");
  const TRANSLATION_JOB_STORAGE_KEY = "velsoftcrm.activeTranslationJob";
  const [translationJob, setTranslationJob] = useState(() => {
    try {
      const stored = window.localStorage.getItem("velsoftcrm.activeTranslationJob");
      return stored ? JSON.parse(stored) : null;
    } catch (_) {
      return null;
    }
  });
  const translationFinalizeRef = useRef(false);

  const [categorySearch, setCategorySearch] = useState("");
  const [subcategorySearch, setSubcategorySearch] = useState("");

  const [categoryPage, setCategoryPage] = useState(1);
  const [subcategoryPage, setSubcategoryPage] = useState(1);

  const [categoryPageSize, setCategoryPageSize] = useState(5);
  const [subcategoryPageSize, setSubcategoryPageSize] = useState(5);

  const [crmPresetKey, setCrmPresetKey] = useState("");
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [storeCatalogEnabled, setStoreCatalogEnabled] = useState(false);
  const [catalogHierarchySaving, setCatalogHierarchySaving] = useState(false);
  const [catalogImportOpen, setCatalogImportOpen] = useState(false);

  useEffect(() => {
    fetchCrmSettings();
    fetchCategories();
  }, []);

  useEffect(() => {
    setCategoryPage(1);
  }, [categorySearch, categoryPageSize]);

  useEffect(() => {
    setSubcategoryPage(1);
  }, [subcategorySearch, subcategoryPageSize, newSubcategory.categoryId]);

  const selectedCategory = useMemo(() => {
    return (
      categories.find(
        (category) => String(category.id) === String(newSubcategory.categoryId)
      ) || null
    );
  }, [categories, newSubcategory.categoryId]);

  const selectedCategoryName = useMemo(() => {
    return getCategoryName(selectedCategory) || "";
  }, [selectedCategory]);

  const editableSubcategoryFields = useMemo(() => {
    return subcategoryFields.filter((field) =>
      field.is_active !== false && !field?.options_json?.shop_system
    );
  }, [subcategoryFields]);

  const visibleSubcategoryFields = useMemo(() => {
    return editableSubcategoryFields.filter((field) => field.is_visible_in_table !== false);
  }, [editableSubcategoryFields]);

  const getSubcategoryBalance = (subcategory) => {
    const count = Number(subcategory?.count || 0);
    const booked = Math.max(Number(subcategory?.booked || 0), 0);

    return Math.max(count - booked, 0);
  };

  const getSubcategoryCustomValue = (subcategory, field) => {
    const values = subcategory?.custom_field_values || {};
    const value = values[field.key];

    if (value === null || value === undefined || value === "") {
      return "—";
    }

    if (field.data_type === "boolean") {
      return value === true || value === "true" || value === "1" ? tt("yes", "Yes") : tt("no", "No");
    }

    return String(value);
  };

  const updateNewSubcategoryCustomValue = (key, value) => {
    setNewSubcategory((prev) => ({
      ...prev,
      custom_field_values: {
        ...(prev.custom_field_values || {}),
        [key]: value,
      },
    }));
  };

  const updateEditingSubcategoryCustomValue = (key, value) => {
    setEditingSubcategory((prev) => ({
      ...prev,
      custom_field_values: {
        ...(prev.custom_field_values || {}),
        [key]: value,
      },
    }));
  };

  const renderCustomFieldInput = (field, value, onChange) => {
    if (field.data_type === "boolean") {
      return (
        <select
          className="cmp-input cmp-select"
          value={value === true || value === "true" || value === "1" ? "true" : value === false || value === "false" || value === "0" ? "false" : ""}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">{tt("select", "Select")}</option>
          <option value="true">{tt("yes", "Yes")}</option>
          <option value="false">{tt("no", "No")}</option>
        </select>
      );
    }

    if (field.data_type === "select") {
      const options = Array.isArray(field.options_json?.options) ? field.options_json.options : [];
      return (
        <select
          className="cmp-input cmp-select"
          value={value || ""}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">{tt("select", "Select")}</option>
          {options.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={field.data_type === "number" ? "number" : field.data_type === "date" ? "date" : "text"}
        className="cmp-input"
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  };

  const categoryLooksLikeWarehouse = (category) => {
    const rawName = toLowerText(category?.name);
    const translatedName = toLowerText(getCategoryName(category));
    const key = toLowerText(category?.key);
    const text = `${rawName} ${translatedName} ${key}`;

    if (NON_WAREHOUSE_CATEGORY_HINTS.some((hint) => text.includes(hint))) {
      return false;
    }

    return WAREHOUSE_CATEGORY_HINTS.some((hint) => text.includes(hint));
  };

  
  const isWarehousePreset = useMemo(() => {
    if (!settingsLoaded) return false;
    return presetLooksLikeWarehouse(crmPresetKey);
  }, [crmPresetKey, settingsLoaded]);

  const selectedCategoryUsesWarehouseFields = useMemo(() => {
    // Wood tenant must keep the original static warehouse behavior.
    // This is not optional and must not depend on selected category.
    if (isWarehousePreset) return true;

    if (!newSubcategory.categoryId) {
      return false;
    }

    return categoryLooksLikeWarehouse(selectedCategory);
  }, [isWarehousePreset, selectedCategory, newSubcategory.categoryId, t]);

  const filteredCategories = useMemo(() => {
    const search = categorySearch.trim().toLowerCase();

    if (!search) return categories;

    return categories.filter((category) => {
      const values = [category.name, getCategoryName(category)];

      return values.some((value) =>
        String(value ?? "").toLowerCase().includes(search)
      );
    });
  }, [categories, categorySearch, t]);

  const filteredSubcategories = useMemo(() => {
    const search = subcategorySearch.trim().toLowerCase();

    if (!search) return subcategories;

    return subcategories.filter((subcategory) => {
      const values = [
        subcategory.name,
        getSubcategoryName(subcategory),
        subcategory.length,
        subcategory.width,
        subcategory.height,
        subcategory.price_per_piece,
        subcategory.count,
        subcategory.booked,
        subcategory.balance,
        getSubcategoryBalance(subcategory),
        subcategory.m3,
      ];

      return values.some((value) =>
        String(value ?? "").toLowerCase().includes(search)
      );
    });
  }, [subcategories, subcategorySearch, t]);

  const categoryTotalPages = Math.max(
    1,
    Math.ceil(filteredCategories.length / categoryPageSize)
  );

  const subcategoryTotalPages = Math.max(
    1,
    Math.ceil(filteredSubcategories.length / subcategoryPageSize)
  );

  const paginatedCategories = useMemo(() => {
    const start = (categoryPage - 1) * categoryPageSize;
    return filteredCategories.slice(start, start + categoryPageSize);
  }, [filteredCategories, categoryPage, categoryPageSize]);

  const paginatedSubcategories = useMemo(() => {
    const start = (subcategoryPage - 1) * subcategoryPageSize;
    return filteredSubcategories.slice(start, start + subcategoryPageSize);
  }, [filteredSubcategories, subcategoryPage, subcategoryPageSize]);

  const categoryShowingFrom =
    filteredCategories.length === 0
      ? 0
      : (categoryPage - 1) * categoryPageSize + 1;

  const categoryShowingTo = Math.min(
    categoryPage * categoryPageSize,
    filteredCategories.length
  );

  const subcategoryShowingFrom =
    filteredSubcategories.length === 0
      ? 0
      : (subcategoryPage - 1) * subcategoryPageSize + 1;

  const subcategoryShowingTo = Math.min(
    subcategoryPage * subcategoryPageSize,
    filteredSubcategories.length
  );


  const fetchSubcategoryFields = async (categoryId) => {
    if (!categoryId) {
      setSubcategoryFields([]);
      return;
    }

    try {
      await api.post("/auth/shop/system-fields/ensure");
      const [categoryResponse, allResponse] = await Promise.all([
        api.get("/custom-fields/subcategory", { params: { category_id: categoryId } }),
        api.get("/custom-fields/subcategory"),
      ]);
      const categoryFields = Array.isArray(categoryResponse.data) ? categoryResponse.data : [];
      const systemFields = (Array.isArray(allResponse.data) ? allResponse.data : []).filter((field) =>
        field?.category_id == null && field?.options_json?.shop_system
      );
      const merged = [...systemFields, ...categoryFields.filter((field) => !systemFields.some((systemField) => systemField.id === field.id))];
      setSubcategoryFields(merged);
    } catch (error) {
      console.error("Error fetching subcategory custom fields", error);
      setSubcategoryFields([]);
    }
  };

  const handleOpenFieldManager = async (category) => {
    setFieldManagerCategory(category);
    setNewCustomField({
      key: "",
      label: "",
      data_type: "text",
      is_required: false,
      is_visible_in_table: true,
    });
    await fetchSubcategoryFields(category.id);
  };

  const handleCreateCustomField = async (event) => {
    event.preventDefault();

    if (!fieldManagerCategory) return;

    if (!newCustomField.key.trim() || !newCustomField.label.trim()) {
      setError(tt("custom_field_required", "Field key and label are required"));
      return;
    }

    setError("");
    setSuccess("");

    try {
      await api.post("/custom-fields", {
        entity_type: "subcategory",
        category_id: fieldManagerCategory.id,
        key: newCustomField.key.trim().toLowerCase().replace(/\s+/g, "_"),
        label: newCustomField.label.trim(),
        data_type: newCustomField.data_type,
        is_required: newCustomField.is_required,
        is_visible_in_table: newCustomField.is_visible_in_table,
        is_searchable: true,
        is_active: true,
      });

      setSuccess(tt("custom_field_created", "Custom field created"));
      setNewCustomField({
        key: "",
        label: "",
        data_type: "text",
        is_required: false,
        is_visible_in_table: true,
      });
      await fetchSubcategoryFields(fieldManagerCategory.id);
    } catch (error) {
      setError(error.response?.data?.detail || tt("error_creating_custom_field", "Error creating custom field"));
      console.error(error);
    }
  };

  const handleDeleteCustomField = async (fieldId) => {
    if (!window.confirm(tt("confirm_delete_custom_field", "Delete this custom field?"))) return;

    setError("");
    setSuccess("");

    try {
      await api.delete(`/custom-fields/${fieldId}`);
      setSuccess(tt("custom_field_deleted", "Custom field deleted"));
      if (fieldManagerCategory) {
        await fetchSubcategoryFields(fieldManagerCategory.id);
      }
    } catch (error) {
      setError(error.response?.data?.detail || tt("error_deleting_custom_field", "Error deleting custom field"));
      console.error(error);
    }
  };

  const fetchCrmSettings = async () => {
    try {
      const settings = await crmSettingsApi.get();
      setCrmPresetKey(getPresetKeyFromSettings(settings));
      const settingsJson = settings?.settings_json && typeof settings.settings_json === "object" ? settings.settings_json : {};
      const featureVisibility = settingsJson?.feature_visibility && typeof settingsJson.feature_visibility === "object" ? settingsJson.feature_visibility : {};
      const storefront = settingsJson?.storefront && typeof settingsJson.storefront === "object" ? settingsJson.storefront : {};
      setCatalogTranslations(storefront?.catalog_translations && typeof storefront.catalog_translations === "object" ? storefront.catalog_translations : {});
      const retailEnabled = storefront.enabled !== false && featureVisibility.shop_settings !== false;
      const commerceModules = storefront?.commerce_modules && typeof storefront.commerce_modules === "object"
        ? storefront.commerce_modules
        : null;
      const legacySalesMode = String(storefront?.sales_mode || "shop");
      const shopEnabled = commerceModules ? commerceModules.shop !== false : legacySalesMode !== "auction";
      const retailStoreActive = retailEnabled && shopEnabled;
      const catalogEnabled = retailStoreActive && storefront.catalog_hierarchy_enabled === true;
      setRetailShopEnabled(retailStoreActive);
      setStoreCatalogEnabled(catalogEnabled);
      if (catalogEnabled) await fetchCatalogTree();
      else setCatalogTree([]);
    } catch (error) {
      console.error("Error fetching CRM settings", error);
      setCrmPresetKey("");
      setRetailShopEnabled(false);
      setStoreCatalogEnabled(false);
    } finally {
      setSettingsLoaded(true);
    }
  };


  const handleCatalogHierarchyToggle = async (enabled) => {
    if (catalogHierarchySaving) return;
    setCatalogHierarchySaving(true);
    setError("");
    setSuccess("");
    try {
      await crmSettingsApi.update({ settings_json: { storefront: { catalog_hierarchy_enabled: enabled } } });
      setStoreCatalogEnabled(enabled);
      if (enabled) await fetchCatalogTree();
      else setCatalogTree([]);
      setSuccess(enabled
        ? tt("catalog_hierarchy_enabled_message", "Extended catalog hierarchy enabled")
        : tt("catalog_hierarchy_disabled_message", "Classic catalog hierarchy restored"));
    } catch (error) {
      setError(error.response?.data?.detail || tt("catalog_hierarchy_save_error", "Could not save catalog hierarchy setting"));
    } finally {
      setCatalogHierarchySaving(false);
    }
  };
  const fetchCategories = async () => {
    try {
      const response = await api.get("/auth/category/all", {
        params: { skip: 0, limit: 2000 },
      });
      setCategories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error(t("error_fetching_categories"), error);
    }
  };

  const fetchCatalogTree = async () => {
    try {
      const response = await api.get("/auth/catalog/tree");
      setCatalogTree(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching catalog tree", error);
      setCatalogTree([]);
    }
  };

  const catalogOptions = flattenCatalogTree(catalogTree);

  const catalogNodeMap = new Map(catalogOptions.map((node) => [Number(node.id), node]));
  const catalogRootSections = Array.isArray(catalogTree) ? catalogTree : [];

  const getCatalogLocalizedName = (node, lang = catalogLanguage) => {
    const row = catalogTranslations?.[String(node?.id)] || catalogTranslations?.[node?.id] || {};
    const exact = String(row?.[lang] || "").trim();
    if (exact) return exact;
    if (lang === "ru" && String(node?.name || "").trim()) return String(node.name).trim();
    const fallback = [row?.ru, row?.et, row?.en, node?.name].find((value) => String(value || "").trim());
    return String(fallback || "").trim();
  };

  const filteredCatalogOptions = useMemo(() => {
    const query = String(catalogMenuSearch || "").trim().toLowerCase();
    if (!query) return catalogOptions;
    return catalogOptions.filter((node) => {
      const names = [node?.name, getCatalogLocalizedName(node, "ru"), getCatalogLocalizedName(node, "et"), getCatalogLocalizedName(node, "en")];
      return names.some((value) => String(value || "").toLowerCase().includes(query));
    });
  }, [catalogOptions, catalogMenuSearch, catalogTranslations]);

  const saveCatalogTranslations = async (nextTranslations) => {
    await crmSettingsApi.update({ settings_json: { storefront: { catalog_translations: nextTranslations } } });
    setCatalogTranslations(nextTranslations);
  };

  const editCatalogTranslation = async (node) => {
    const current = getCatalogLocalizedName(node, catalogLanguage);
    const langLabel = catalogLanguage === "ru" ? "RU" : catalogLanguage === "et" ? "ET" : "EN";
    const value = window.prompt(`${tt("catalog_translation_edit", "Название категории")} (${langLabel})`, current);
    if (value === null) return;
    const cleaned = String(value || "").trim();
    if (!cleaned) return;
    const next = { ...catalogTranslations, [String(node.id)]: { ...(catalogTranslations?.[String(node.id)] || {}), [catalogLanguage]: cleaned } };
    try {
      setError("");
      await saveCatalogTranslations(next);
      setSuccess(tt("catalog_translation_saved", "Перевод меню сохранён"));
    } catch (error) {
      setError(error.response?.data?.detail || tt("catalog_translation_save_error", "Не удалось сохранить перевод меню"));
    }
  };

  const persistTranslationJob = (job) => {
    setTranslationJob(job);
    try {
      if (job) window.localStorage.setItem(TRANSLATION_JOB_STORAGE_KEY, JSON.stringify(job));
      else window.localStorage.removeItem(TRANSLATION_JOB_STORAGE_KEY);
    } catch (_) {
      // localStorage may be unavailable in privacy mode.
    }
  };

  const startPersistentTranslation = async ({ kind, sourceLang, targetLang, texts, subcategoryId = null }) => {
    const response = await api.post("/auth/shop/translate/job", {
      source_lang: sourceLang,
      target_lang: targetLang,
      texts,
    });
    const job = {
      jobId: response.data?.job_id,
      kind,
      sourceLang,
      targetLang,
      subcategoryId: subcategoryId || null,
      progress: Number(response.data?.progress || 0),
      current: Number(response.data?.current || 0),
      total: Number(response.data?.total || Object.keys(texts || {}).length || 0),
      status: response.data?.status || "queued",
      translations: {},
      error: null,
    };
    translationFinalizeRef.current = false;
    persistTranslationJob(job);
    return job;
  };

  const applyFinishedTranslation = async (job, statusPayload) => {
    const translated = statusPayload?.translations || {};
    if (job.kind === "catalog") {
      const next = { ...catalogTranslations };
      Object.entries(translated).forEach(([key, value]) => {
        const match = /^catalog_(\d+)$/.exec(String(key));
        if (!match || !String(value || "").trim()) return;
        const id = match[1];
        next[id] = { ...(next[id] || {}), [job.targetLang]: String(value).trim() };
      });
      await saveCatalogTranslations(next);
      setCatalogLanguage(job.targetLang);
      setCatalogTranslationBusy("");
      setSuccess(tt("catalog_translate_done", "Меню переведено. При необходимости отредактируйте названия вручную."));
      return;
    }

    if (job.kind === "shop") {
      const next = { ...shopContent };
      next[`shop_short_description_${job.targetLang}`] = translated.short_description || "";
      next[`shop_description_${job.targetLang}`] = translated.full_description || "";
      next[`shop_delivery_${job.targetLang}`] = translated.delivery || "";
      next[`shop_payment_${job.targetLang}`] = translated.payment || "";
      next[`shop_seo_title_${job.targetLang}`] = translated.seo_title || "";
      next[`shop_seo_description_${job.targetLang}`] = translated.seo_description || "";

      let faqItems = parseFaq().map((item, index) => ({
        ...item,
        [`question_${job.targetLang}`]: translated[`faq_${index}_question`] || "",
        [`answer_${job.targetLang}`]: translated[`faq_${index}_answer`] || "",
      }));
      next.shop_faq_json = JSON.stringify(faqItems);
      setShopContent(next);
      setContentLanguage(job.targetLang);
      setShopTranslationBusy("");

      // If the page was reloaded while translating, preserve the finished translation
      // directly on the existing product instead of losing the result.
      if (job.subcategoryId && (!contentManagerSubcategory || Number(contentManagerSubcategory.id) !== Number(job.subcategoryId))) {
        try {
          const currentResponse = await api.get(`/auth/subcategory/${job.subcategoryId}/shop-content`);
          const currentValues = currentResponse.data?.values || {};
          const serverFaq = (() => {
            try {
              const parsed = JSON.parse(currentValues.shop_faq_json || "[]");
              return Array.isArray(parsed) ? parsed : [];
            } catch (_) {
              return [];
            }
          })();
          const mergedFaq = serverFaq.map((item, index) => ({
            ...item,
            [`question_${job.targetLang}`]: translated[`faq_${index}_question`] || item?.[`question_${job.targetLang}`] || "",
            [`answer_${job.targetLang}`]: translated[`faq_${index}_answer`] || item?.[`answer_${job.targetLang}`] || "",
          }));
          await api.put(`/auth/subcategory/${job.subcategoryId}/shop-content`, {
            values: {
              ...currentValues,
              [`shop_short_description_${job.targetLang}`]: translated.short_description || "",
              [`shop_description_${job.targetLang}`]: translated.full_description || "",
              [`shop_delivery_${job.targetLang}`]: translated.delivery || "",
              [`shop_payment_${job.targetLang}`]: translated.payment || "",
              [`shop_seo_title_${job.targetLang}`]: translated.seo_title || "",
              [`shop_seo_description_${job.targetLang}`]: translated.seo_description || "",
              shop_faq_json: JSON.stringify(mergedFaq),
            },
          });
        } catch (saveError) {
          console.error("Could not persist translated shop content after reload", saveError);
        }
      }

      setSuccess(tt("shop_translate_done", "Перевод готов. Проверьте текст и сохраните товар."));
    }
  };

  useEffect(() => {
    if (!translationJob?.jobId) return undefined;

    let cancelled = false;
    let timer = null;

    const poll = async () => {
      try {
        const response = await api.get(`/auth/shop/translate/job/${translationJob.jobId}`);
        if (cancelled) return;
        const status = response.data || {};
        const nextJob = {
          ...translationJob,
          progress: Number(status.progress || 0),
          current: Number(status.current || 0),
          total: Number(status.total || translationJob.total || 0),
          status: status.status || translationJob.status,
          translations: status.translations || {},
          error: status.error || null,
        };
        persistTranslationJob(nextJob);

        if (status.status === "completed") {
          if (!translationFinalizeRef.current) {
            translationFinalizeRef.current = true;
            try {
              await applyFinishedTranslation(nextJob, status);
            } finally {
              persistTranslationJob(null);
              translationFinalizeRef.current = false;
            }
          }
          return;
        }

        if (status.status === "failed") {
          setShopTranslationBusy("");
          setCatalogTranslationBusy("");
          setError(status.error || tt("shop_translate_error", "Не удалось выполнить автоматический перевод"));
          persistTranslationJob(null);
          return;
        }

        timer = window.setTimeout(poll, 650);
      } catch (pollError) {
        // Network hiccup/reload should not cancel the server-side job.
        timer = window.setTimeout(poll, 1300);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [translationJob?.jobId]);

  const autoTranslateCatalogMenu = async (targetLang) => {
    const sourceLang = catalogLanguage;
    if (!targetLang || targetLang === sourceLang || catalogTranslationBusy || !catalogOptions.length) return;
    const texts = {};
    catalogOptions.forEach((node) => {
      const value = getCatalogLocalizedName(node, sourceLang);
      if (value) texts[`catalog_${node.id}`] = value;
    });
    if (!Object.keys(texts).length) {
      setError(tt("catalog_translate_source_empty", "Нет названий для перевода"));
      return;
    }
    try {
      setError("");
      setCatalogTranslationBusy(targetLang);
      await startPersistentTranslation({
        kind: "catalog",
        sourceLang,
        targetLang,
        texts,
      });
    } catch (error) {
      setCatalogTranslationBusy("");
      setError(error.response?.data?.detail || tt("catalog_translate_error", "Не удалось автоматически перевести меню"));
    }
  };

  const getCatalogSectionForCategory = (category) => {
    let node = catalogNodeMap.get(Number(category?.catalog_category_id));
    if (!node) return null;
    let guard = 0;
    while (node?.parent_id && guard < 20) {
      const parent = catalogNodeMap.get(Number(node.parent_id));
      if (!parent) break;
      node = parent;
      guard += 1;
    }
    return node || null;
  };

  const handleCreateCatalogNode = async (event) => {
    event.preventDefault();
    const name = String(catalogDraft.name || "").trim();
    if (!name || catalogBusy) return;
    setCatalogBusy(true);
    setError("");
    try {
      await api.post("/auth/catalog/add", {
        name,
        parent_id: null,
        sort_order: catalogOptions.length,
        is_active: true,
      });
      setCatalogDraft({ name: "", parent_id: "" });
      await fetchCatalogTree();
      setSuccess(t("catalog_node_created"));
    } catch (error) {
      setError(error.response?.data?.detail || t("catalog_node_create_error"));
    } finally {
      setCatalogBusy(false);
    }
  };

  const handleRenameCatalogNode = async (node) => {
    const nextName = window.prompt(t("catalog_rename_prompt"), node.name);
    if (nextName === null || !String(nextName).trim()) return;
    try {
      await api.put(`/auth/catalog/${node.id}`, { name: String(nextName).trim() });
      await fetchCatalogTree();
    } catch (error) {
      setError(error.response?.data?.detail || t("catalog_node_update_error"));
    }
  };

  const handleDeleteCatalogNode = async (node) => {
    if (!window.confirm(t("catalog_delete_confirm", { name: node.name }))) return;
    try {
      await api.delete(`/auth/catalog/${node.id}`);
      await fetchCatalogTree();
    } catch (error) {
      setError(error.response?.data?.detail || t("catalog_node_delete_error"));
    }
  };


  const handleDownloadCatalogTemplate = async () => {
    try {
      const response = await api.get("/auth/catalog/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "catalog_import_template.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setError(error.response?.data?.detail || t("catalog_csv_template_error"));
    }
  };

  const handleImportCatalogCsv = async () => {
    if (!catalogImportFile || catalogImportBusy) return;
    if (catalogImportMode === "replace" && !window.confirm(t("catalog_csv_replace_confirm"))) return;

    setCatalogImportBusy(true);
    setError("");
    setSuccess("");
    try {
      const formData = new FormData();
      formData.append("file", catalogImportFile);
      const response = await api.post(`/auth/catalog/import-csv?mode=${catalogImportMode}`, formData);
      const data = response.data || {};
      setSuccess(t("catalog_csv_import_success", { rows: data.rows || 0, created: data.created || 0, updated: data.updated || 0 }));
      setCatalogImportFile(null);
      const input = document.getElementById("catalog-csv-input");
      if (input) input.value = "";
      await Promise.all([fetchCatalogTree(), fetchCategories(), fetchSubcategories()]);
      setCatalogImportOpen(false);
    } catch (error) {
      setError(error.response?.data?.detail || t("catalog_csv_import_error"));
    } finally {
      setCatalogImportBusy(false);
    }
  };

  const handleSyncCatalogLegacy = async () => {
    if (catalogSyncBusy) return;
    setCatalogSyncBusy(true);
    setError("");
    setSuccess("");
    try {
      const response = await api.post("/auth/catalog/sync-legacy");
      const data = response.data || {};
      const categoryResponse = await api.get("/auth/category/all", { params: { skip: 0, limit: 2000 } });
      const nextCategories = Array.isArray(categoryResponse.data) ? categoryResponse.data : [];
      setCategories(nextCategories);
      await fetchSubcategories();
      if (!newSubcategory.categoryId && nextCategories.length) {
        setNewSubcategory((prev) => ({ ...prev, categoryId: String(nextCategories[0].id) }));
      }
      setSuccess(t("catalog_legacy_sync_success", {
        categories: data.legacy_categories_created || 0,
        reused: data.legacy_categories_reused || 0,
        cleaned: data.legacy_subcategories_removed || 0,
      }));
    } catch (error) {
      setError(error.response?.data?.detail || t("catalog_legacy_sync_error"));
    } finally {
      setCatalogSyncBusy(false);
    }
  };

  const fetchSubcategories = async () => {
    try {
      const response = await api.get("/auth/subcategory/all", {
        params: { skip: 0, limit: 2000 },
      });
      setSubcategories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error(t("error_fetching_subcategories"), error);
    }
  };

  const handleCreateCategory = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!newCategory.trim()) {
      setError(t("category_name_required"));
      return;
    }

    try {
      await api.post("/auth/category/add", { name: newCategory.trim(), catalog_category_id: storeCatalogEnabled && newCategoryCatalogId ? Number(newCategoryCatalogId) : null });
      setSuccess(t("category_created"));
      setNewCategory("");
      setNewCategoryCatalogId("");
      fetchCategories();
    } catch (error) {
      setError(error.response?.data?.detail || t("error_creating_category"));
      console.error(error);
    }
  };

  const revokePhotoUrls = (items = []) => {
    items.forEach((item) => {
      const url = typeof item === "string" ? item : item?.preview_url;
      if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
    });
  };

  const loadProductPhotos = async (subcategory) => {
    if (!subcategory?.id) return;
    setIsLoadingPhotos(true);
    try {
      const response = await api.get("/auth/storage/files", {
        params: { entity_type: "subcategory", entity_id: subcategory.id },
      });
      const imageFiles = (Array.isArray(response.data) ? response.data : []).filter(
        (item) => String(item?.mime_type || "").startsWith("image/")
      );
      const hydrated = await Promise.all(
        imageFiles.map(async (item) => {
          try {
            const imageResponse = await api.get(`/auth/storage/files/${item.id}/download`, {
              responseType: "blob",
            });
            return { ...item, preview_url: URL.createObjectURL(imageResponse.data) };
          } catch (downloadError) {
            console.error("Could not load product photo", downloadError);
            return { ...item, preview_url: "" };
          }
        })
      );
      setProductPhotos((previous) => {
        revokePhotoUrls(previous);
        return hydrated;
      });
      setActivePhotoIndex(0);
    } catch (loadError) {
      setError(loadError.response?.data?.detail || tt("product_photos_load_error", "Could not load product photos"));
      console.error(loadError);
    } finally {
      setIsLoadingPhotos(false);
    }
  };

  const openPhotoManager = async (subcategory) => {
    setError("");
    setSuccess("");
    setPhotoManagerSubcategory(subcategory);
    setPhotoUploadQueue([]);
    await loadProductPhotos(subcategory);
  };

  const loadShopTemplates = async () => {
    try {
      const response = await api.get("/auth/shop/content-templates");
      const data = response.data || {};
      setShopTemplates({
        delivery: Array.isArray(data.delivery) ? data.delivery : [],
        payment: Array.isArray(data.payment) ? data.payment : [],
        faq: Array.isArray(data.faq) ? data.faq : [],
      });
    } catch (templateError) {
      console.error("Could not load shop templates", templateError);
    }
  };

  const firstTranslatedValue = (map = {}) => {
    if (!map || typeof map !== "object") return "";
    return map[contentLanguage] || map.ru || map.et || map.en || Object.values(map).find(Boolean) || "";
  };

  const applyContentTemplate = (kind) => {
    const templateId = selectedShopTemplate[kind];
    const template = (shopTemplates[kind] || []).find((item) => String(item.id) === String(templateId));
    if (!template) return;
    if (kind === "delivery" || kind === "payment") {
      const next = { ...shopContent };
      ["ru", "et", "en"].forEach((lang) => {
        const text = template.values?.[lang] || "";
        next[`shop_${kind}_${lang}`] = text;
      });
      setShopContent(next);
      return;
    }
    const items = parseFaq();
    items.push({
      question_ru: template.question?.ru || "",
      answer_ru: template.answer?.ru || "",
      question_et: template.question?.et || "",
      answer_et: template.answer?.et || "",
      question_en: template.question?.en || "",
      answer_en: template.answer?.en || "",
    });
    updateShopContent("shop_faq_json", JSON.stringify(items));
  };

  const saveContentTemplate = async (kind, faqIndex = null) => {
    if (shopTemplateBusy) return;
    let payload = {};
    if (kind === "delivery" || kind === "payment") {
      const values = {
        ru: String(shopContent[`shop_${kind}_ru`] || "").trim(),
        et: String(shopContent[`shop_${kind}_et`] || "").trim(),
        en: String(shopContent[`shop_${kind}_en`] || "").trim(),
      };
      const preview = firstTranslatedValue(values);
      if (!preview) {
        setError(tt("template_empty", "Сначала заполните текст шаблона"));
        return;
      }
      payload = { name: preview.split("\n")[0].slice(0, 60), values };
    } else {
      const item = parseFaq()[faqIndex];
      if (!item) return;
      const question = { ru: item.question_ru || "", et: item.question_et || "", en: item.question_en || "" };
      const answer = { ru: item.answer_ru || "", et: item.answer_et || "", en: item.answer_en || "" };
      const preview = firstTranslatedValue(question) || firstTranslatedValue(answer);
      if (!preview) {
        setError(tt("template_empty", "Сначала заполните вопрос или ответ"));
        return;
      }
      payload = { name: preview.slice(0, 60), question, answer };
    }
    try {
      setShopTemplateBusy(`${kind}:${faqIndex ?? "main"}`);
      setError("");
      await api.post(`/auth/shop/content-templates/${kind}`, payload);
      await loadShopTemplates();
      setSuccess(tt("template_saved", "Шаблон сохранён и доступен для других товаров"));
    } catch (templateError) {
      setError(templateError.response?.data?.detail || tt("template_save_error", "Не удалось сохранить шаблон"));
    } finally {
      setShopTemplateBusy("");
    }
  };

  const deleteSelectedContentTemplate = async (kind) => {
    const templateId = selectedShopTemplate[kind];
    const template = (shopTemplates[kind] || []).find((item) => String(item.id) === String(templateId));
    if (!template || template.builtin) return;
    if (!window.confirm(tt("delete_template_confirm", "Удалить выбранный шаблон?"))) return;
    try {
      setShopTemplateBusy(`${kind}:delete`);
      await api.delete(`/auth/shop/content-templates/${kind}/${templateId}`);
      setSelectedShopTemplate((prev) => ({ ...prev, [kind]: "" }));
      await loadShopTemplates();
    } catch (templateError) {
      setError(templateError.response?.data?.detail || tt("template_delete_error", "Не удалось удалить шаблон"));
    } finally {
      setShopTemplateBusy("");
    }
  };

  const openContentManager = async (subcategory) => {
    setError("");
    setSuccess("");
    setContentManagerSubcategory(subcategory);
    setContentLanguage("ru");
    setSelectedShopTemplate({ delivery: "", payment: "", faq: "" });
    setIsLoadingContent(true);
    await loadShopTemplates();
    try {
      const response = await api.get(`/auth/subcategory/${subcategory.id}/shop-content`);
      setShopContent({ ...makeEmptyShopContent(), ...(response.data?.values || {}) });
    } catch (loadError) {
      setError(loadError.response?.data?.detail || tt("shop_content_load_error", "Could not load shop product content"));
    } finally {
      setIsLoadingContent(false);
    }
  };

  const updateShopContent = (key, value) => {
    setShopContent((prev) => ({ ...prev, [key]: value }));
  };

  const parseFaq = () => {
    try {
      const parsed = JSON.parse(shopContent.shop_faq_json || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const updateFaq = (index, key, value) => {
    const items = parseFaq();
    while (items.length <= index) items.push({ question_ru: "", answer_ru: "", question_et: "", answer_et: "", question_en: "", answer_en: "" });
    items[index] = { ...items[index], [key]: value };
    updateShopContent("shop_faq_json", JSON.stringify(items));
  };

  const addFaq = () => {
    const items = parseFaq();
    items.push({ question_ru: "", answer_ru: "", question_et: "", answer_et: "", question_en: "", answer_en: "" });
    updateShopContent("shop_faq_json", JSON.stringify(items));
  };

  const removeFaq = (index) => {
    const items = parseFaq().filter((_, itemIndex) => itemIndex !== index);
    updateShopContent("shop_faq_json", JSON.stringify(items));
  };

  const getTranslatableShopFields = (sourceLang) => {
    const texts = {
      short_description: shopContent[`shop_short_description_${sourceLang}`] || "",
      full_description: shopContent[`shop_description_${sourceLang}`] || "",
      delivery: shopContent[`shop_delivery_${sourceLang}`] || "",
      payment: shopContent[`shop_payment_${sourceLang}`] || "",
      seo_title: shopContent[`shop_seo_title_${sourceLang}`] || "",
      seo_description: shopContent[`shop_seo_description_${sourceLang}`] || "",
    };
    parseFaq().forEach((item, index) => {
      texts[`faq_${index}_question`] = item[`question_${sourceLang}`] || "";
      texts[`faq_${index}_answer`] = item[`answer_${sourceLang}`] || "";
    });
    return texts;
  };

  const targetLanguageHasContent = (targetLang) => {
    const directKeys = [
      `shop_short_description_${targetLang}`,
      `shop_description_${targetLang}`,
      `shop_delivery_${targetLang}`,
      `shop_payment_${targetLang}`,
      `shop_seo_title_${targetLang}`,
      `shop_seo_description_${targetLang}`,
    ];
    if (directKeys.some((key) => String(shopContent[key] || "").trim())) return true;
    return parseFaq().some((item) =>
      String(item[`question_${targetLang}`] || "").trim() ||
      String(item[`answer_${targetLang}`] || "").trim()
    );
  };

  const autoTranslateShopContent = async (targetLang) => {
    const sourceLang = contentLanguage;
    if (!targetLang || targetLang === sourceLang || shopTranslationBusy) return;

    const texts = getTranslatableShopFields(sourceLang);
    const hasSourceText = Object.values(texts).some((value) => String(value || "").trim());
    if (!hasSourceText) {
      setError(tt("shop_translate_source_empty", "Сначала заполните текст на текущем языке"));
      return;
    }

    if (targetLanguageHasContent(targetLang)) {
      const languageLabel = targetLang === "ru" ? "RU" : targetLang === "et" ? "ET" : "EN";
      if (!window.confirm(tt("shop_translate_overwrite_confirm", `В ${languageLabel} уже есть текст. Заменить его автоматическим переводом?`))) return;
    }

    try {
      setError("");
      setShopTranslationBusy(targetLang);
      await startPersistentTranslation({
        kind: "shop",
        sourceLang,
        targetLang,
        texts,
        subcategoryId: contentManagerSubcategory?.id || null,
      });
    } catch (translateError) {
      setShopTranslationBusy("");
      setError(translateError.response?.data?.detail || tt("shop_translate_error", "Не удалось выполнить автоматический перевод"));
    }
  };

  const saveShopContent = async () => {
    if (!contentManagerSubcategory?.id || isSavingContent) return;
    const missing = [];
    const hasShortDescription = ["ru", "et", "en"].some((lang) => String(shopContent[`shop_short_description_${lang}`] || "").trim());
    const hasFullDescription = ["ru", "et", "en"].some((lang) => String(shopContent[`shop_description_${lang}`] || "").trim());
    if (!hasShortDescription) missing.push(tt("short_description", "Краткое описание"));
    if (!hasFullDescription) missing.push(tt("full_description", "Полное описание"));
    if (missing.length) {
      setError(`${tt("shop_required_fields_missing", "Заполните обязательные поля магазина")}: ${missing.join(", ")}`);
      return;
    }
    setIsSavingContent(true);
    setError("");
    try {
      await api.put(`/auth/subcategory/${contentManagerSubcategory.id}/shop-content`, { values: shopContent });
      setSuccess(tt("shop_content_saved", "Shop product content saved"));
      setContentManagerSubcategory(null);
    } catch (saveError) {
      setError(saveError.response?.data?.detail || tt("shop_content_save_error", "Could not save shop product content"));
    } finally {
      setIsSavingContent(false);
    }
  };

  const closePhotoManager = () => {
    revokePhotoUrls(productPhotos);
    setProductPhotos([]);
    setPhotoUploadQueue([]);
    setPhotoManagerSubcategory(null);
    setActivePhotoIndex(0);
  };

  const handleGalleryFilesChange = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length || isSavingPhotos) return;

    const validFiles = [];
    for (const file of files) {
      if (!String(file.type || "").startsWith("image/")) {
        setError(tt("product_photo_invalid", "Please select image files only"));
        continue;
      }
      if (file.size > 15 * 1024 * 1024) {
        setError(tt("product_photo_too_large", "Each image must be smaller than 15 MB"));
        continue;
      }
      validFiles.push(file);
    }

    if (!validFiles.length) return;
    setError("");
    await saveProductPhotos(validFiles);
  };

  const saveProductPhotos = async (filesToUpload = []) => {
    const uploadFiles = Array.from(filesToUpload || []);
    if (!photoManagerSubcategory?.id || !uploadFiles.length || isSavingPhotos) return;

    const queue = uploadFiles.map((file, index) => ({
      id: `${Date.now()}-${index}-${Math.random().toString(16).slice(2)}`,
      name: file.name || `Photo ${index + 1}`,
      size: file.size || 0,
      progress: 0,
      status: "queued",
      error: "",
    }));
    setPhotoUploadQueue(queue);
    setIsSavingPhotos(true);
    setError("");

    let uploadedCount = 0;
    for (let index = 0; index < uploadFiles.length; index += 1) {
      const file = uploadFiles[index];
      const queueId = queue[index].id;
      const formData = new FormData();
      formData.append("file", file);

      try {
        setPhotoUploadQueue((previous) => previous.map((item) =>
          item.id === queueId ? { ...item, status: "uploading", progress: Math.max(1, item.progress) } : item
        ));
        await api.post(
          `/auth/storage/files?entity_type=subcategory&entity_id=${photoManagerSubcategory.id}`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (event) => {
              const total = Number(event.total || file.size || 0);
              const loaded = Number(event.loaded || 0);
              const progress = total ? Math.min(99, Math.round((loaded / total) * 100)) : 50;
              setPhotoUploadQueue((previous) => previous.map((item) =>
                item.id === queueId ? { ...item, status: "uploading", progress } : item
              ));
            },
          }
        );
        uploadedCount += 1;
        setPhotoUploadQueue((previous) => previous.map((item) =>
          item.id === queueId ? { ...item, status: "done", progress: 100 } : item
        ));
      } catch (saveError) {
        const message = saveError.response?.data?.detail || tt("product_photos_save_error", "Could not save product photo");
        setPhotoUploadQueue((previous) => previous.map((item) =>
          item.id === queueId ? { ...item, status: "failed", error: String(message), progress: item.progress || 0 } : item
        ));
      }
    }

    if (uploadedCount > 0) {
      setSuccess(tt("product_photos_saved", "Product photos saved"));
      await loadProductPhotos(photoManagerSubcategory);
      await fetchSubcategories();
    }

    const failedCount = uploadFiles.length - uploadedCount;
    if (failedCount > 0) {
      setError(tt("product_photos_partial_error", `${failedCount} photo(s) could not be uploaded`));
    }
    setIsSavingPhotos(false);
    if (!failedCount) window.setTimeout(() => setPhotoUploadQueue([]), 1200);
  };

  const deleteProductPhoto = async (photo) => {
    if (!photo?.id || !window.confirm(tt("confirm_delete_product_photo", "Delete this product photo?"))) return;
    try {
      await api.delete(`/auth/storage/files/${photo.id}`);
      setSuccess(tt("product_photo_deleted", "Product photo deleted"));
      await loadProductPhotos(photoManagerSubcategory);
      await fetchSubcategories();
    } catch (deleteError) {
      setError(deleteError.response?.data?.detail || tt("product_photo_delete_error", "Could not delete product photo"));
      console.error(deleteError);
    }
  };

  const downloadProductPhoto = async (photo) => {
    if (!photo?.id || downloadingProductPhotoId) return;
    try {
      setDownloadingProductPhotoId(photo.id);
      const response = await api.get(`/auth/storage/files/${photo.id}/download`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = photo.file_name || "photo";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (downloadError) {
      setError(downloadError.response?.data?.detail || tt("product_photo_download_error", "Could not download product photo"));
    } finally {
      setDownloadingProductPhotoId(null);
    }
  };

  const renameProductPhoto = async (photo) => {
    if (!photo?.id || renamingProductPhotoId) return;
    const nextName = window.prompt(tt("product_photo_new_name", "New photo name"), photo.file_name || "photo");
    if (nextName === null) return;
    const cleanName = String(nextName || "").trim();
    if (!cleanName) return;
    try {
      setRenamingProductPhotoId(photo.id);
      await api.patch(`/auth/storage/files/${photo.id}`, { file_name: cleanName });
      setSuccess(tt("product_photo_renamed", "Product photo renamed"));
      await loadProductPhotos(photoManagerSubcategory);
      await fetchSubcategories();
    } catch (renameError) {
      setError(renameError.response?.data?.detail || tt("product_photo_rename_error", "Could not rename product photo"));
    } finally {
      setRenamingProductPhotoId(null);
    }
  };

  const productPhotoUploadProgress = photoUploadQueue.length
    ? Math.round(photoUploadQueue.reduce((sum, item) => sum + Number(item.progress || 0), 0) / photoUploadQueue.length)
    : 0;

  const productImageUrl = (subcategory) => {
    if (!subcategory?.image_attachment_id) return "";
    const base = String(api.defaults.baseURL || "").replace(/\/+$/, "");
    return `${base}/public/shop/products/${subcategory.id}/image`;
  };

  const validateBasicProductDraft = () => {
    if (!newSubcategory.name.trim() || !newSubcategory.categoryId) {
      setError(t("subcategory_name_required"));
      return false;
    }
    if (String(newSubcategory.price_per_piece ?? "").trim() === "") {
      setError(tt("product_price_required", "Укажите цену товара"));
      return false;
    }
    if (String(newSubcategory.count ?? "").trim() === "") {
      setError(tt("product_count_required", "Укажите количество товара"));
      return false;
    }
    return true;
  };

  const createSubcategoryRecord = async (shopValues = null) => {
    const isWoodLike = selectedCategoryUsesWarehouseFields;
    setIsCreatingSubcategory(true);
    setError("");
    setSuccess("");

    try {
      const response = await api.post("/auth/subcategory/add", {
        name: newSubcategory.name.trim(),
        count: String(newSubcategory.count || "0").trim(),
        length: isWoodLike ? String(newSubcategory.length || "1").trim() : "1",
        width: isWoodLike ? String(newSubcategory.width || "1").trim() : "1",
        height: isWoodLike ? String(newSubcategory.height || "1").trim() : "1",
        price_per_piece: String(newSubcategory.price_per_piece || "0").trim(),
        category_id: newSubcategory.categoryId,
        catalog_category_id: storeCatalogEnabled ? null : (newSubcategory.catalogCategoryId ? Number(newSubcategory.catalogCategoryId) : null),
        custom_field_values: newSubcategory.custom_field_values || {},
      });

      const createdId = response.data.subcategory_id || response.data.id;

      if (retailShopEnabled && shopValues) {
        await api.put(`/auth/subcategory/${createdId}/shop-content`, { values: shopValues });
      }

      setSuccess(retailShopEnabled
        ? tt("product_created_store_ready", "Товар создан, данные интернет-магазина сохранены")
        : t("subcategory_created"));

      setCreateShopModalOpen(false);
      setCreateShopAdvancedOpen(false);
      setShopContent(makeEmptyShopContent());

      setNewSubcategory({
        name: "",
        count: "",
        length: "",
        width: "",
        height: "",
        price_per_piece: "",
        categoryId: newSubcategory.categoryId,
        catalogCategoryId: newSubcategory.catalogCategoryId,
        custom_field_values: {},
      });

      await fetchSubcategories();
    } catch (error) {
      setError(error.response?.data?.detail || t("error_creating_subcategory"));
      console.error(error);
    } finally {
      setIsCreatingSubcategory(false);
    }
  };

  const handleCreateSubcategory = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (retailShopEnabled) {
      setShopContent(makeEmptyShopContent());
      setContentLanguage("ru");
      setCreateShopAdvancedOpen(false);
      setCreateShopModalOpen(true);
      return;
    }

    if (!validateBasicProductDraft()) return;
    await createSubcategoryRecord(null);
  };

  const handleConfirmCreateShopProduct = async () => {
    if (!validateBasicProductDraft()) return;
    const missing = [];
    const hasShortDescription = ["ru", "et", "en"].some((lang) => String(shopContent[`shop_short_description_${lang}`] || "").trim());
    const hasFullDescription = ["ru", "et", "en"].some((lang) => String(shopContent[`shop_description_${lang}`] || "").trim());
    if (!hasShortDescription) missing.push(tt("short_description", "Краткое описание"));
    if (!hasFullDescription) missing.push(tt("full_description", "Полное описание"));
    const missingCustom = editableSubcategoryFields
      .filter((field) => field.is_required)
      .filter((field) => {
        const value = newSubcategory.custom_field_values?.[field.key];
        return value === null || value === undefined || String(value).trim() === "";
      })
      .map((field) => field.label || field.key);
    missing.push(...missingCustom);
    if (missing.length) {
      setError(`${tt("shop_required_fields_missing", "Заполните обязательные поля магазина")}: ${missing.join(", ")}`);
      return;
    }
    await createSubcategoryRecord(shopContent);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm(tt(
      "confirm_delete_category_cascade",
      "Delete this category? Its subcategories and linked order/product rows will also be removed."
    ))) return;

    setError("");
    setSuccess("");

    try {
      await api.delete(`/auth/category/by-id/${categoryId}`);
      if (String(newSubcategory.categoryId) === String(categoryId)) {
        setNewSubcategory((prev) => ({ ...prev, categoryId: "" }));
        setSubcategories([]);
        setSubcategoryFields([]);
      }
      setSuccess(t("category_deleted"));
      await fetchCategories();
    } catch (error) {
      setError(error.response?.data?.detail || t("error_deleting_category"));
      console.error(error);
    }
  };

  const handleDeleteSubcategory = async (subcategoryId) => {
    if (!window.confirm(tt(
      "confirm_delete_subcategory_cascade",
      "Delete this subcategory? Linked order/product rows will also be removed."
    ))) return;

    setError("");
    setSuccess("");

    try {
      await api.delete(`/auth/subcategory/by-id/${subcategoryId}`);
      setSuccess(t("subcategory_deleted"));
      await fetchSubcategories();
    } catch (error) {
      setError(error.response?.data?.detail || t("error_deleting_subcategory"));
      console.error(error);
    }
  };

  const handleDeleteAllCategories = async () => {
    if (!categories.length) return;
    const accepted = window.confirm(tt(
      "confirm_delete_all_categories",
      `Delete all ${categories.length} categories for this tenant? This also deletes their subcategories and linked order/product rows. This cannot be undone.`
    ));
    if (!accepted) return;

    setError("");
    setSuccess("");
    try {
      const response = await api.delete("/auth/category/all?confirm=true");
      setNewSubcategory((prev) => ({ ...prev, categoryId: "" }));
      setSubcategories([]);
      setSubcategoryFields([]);
      await fetchCategories();
      setSuccess(tt("all_categories_deleted", `Deleted ${response.data?.deleted || 0} categories`));
    } catch (error) {
      setError(error.response?.data?.detail || tt("bulk_delete_error", "Could not delete categories"));
    }
  };

  const handleDeleteAllSubcategories = async () => {
    if (!newSubcategory.categoryId || !subcategories.length) return;
    const accepted = window.confirm(tt(
      "confirm_delete_all_subcategories",
      `Delete all ${subcategories.length} subcategories in this category? Linked order/product rows will also be removed. This cannot be undone.`
    ));
    if (!accepted) return;

    setError("");
    setSuccess("");
    try {
      const response = await api.delete(`/auth/subcategory/all?category_id=${newSubcategory.categoryId}&confirm=true`);
      await fetchSubcategories();
      setSuccess(tt("all_subcategories_deleted", `Deleted ${response.data?.deleted || 0} subcategories`));
    } catch (error) {
      setError(error.response?.data?.detail || tt("bulk_delete_error", "Could not delete subcategories"));
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setUpdatedCategoryName(category.name);
  };

  const handleEditSubcategory = async (subcategory) => {
    setEditingSubcategory({
      ...subcategory,
      custom_field_values: subcategory.custom_field_values || {},
    });
    setUpdatedSubcategoryName(subcategory.name);
    setShopContent(makeEmptyShopContent());
    await fetchSubcategoryFields(subcategory.category_id);

    if (retailShopEnabled) {
      try {
        setIsLoadingContent(true);
        const response = await api.get(`/auth/subcategory/${subcategory.id}/shop-content`);
        setShopContent({ ...makeEmptyShopContent(), ...(response.data?.values || {}) });
      } catch (loadError) {
        setError(loadError.response?.data?.detail || tt("shop_content_load_error", "Не удалось загрузить данные интернет-магазина"));
      } finally {
        setIsLoadingContent(false);
      }
    }
  };

  const handleUpdateCategory = async () => {
    if (!updatedCategoryName.trim()) {
      setError(t("category_name_required"));
      return;
    }

    try {
      await api.put(`/auth/category/${editingCategory.id}`, {
        name: updatedCategoryName.trim(),
        catalog_category_id: storeCatalogEnabled && editingCategory.catalog_category_id ? Number(editingCategory.catalog_category_id) : null,
      });
      setSuccess(t("category_updated"));
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      setError(error.response?.data?.detail || t("error_updating_category"));
      console.error(error);
    }
  };

  const isEditingSubcategoryWarehouseLike = useMemo(() => {
    if (!editingSubcategory) return false;

    const category = categories.find(
      (item) => String(item.id) === String(editingSubcategory.category_id)
    );

    return categoryLooksLikeWarehouse(category);
  }, [editingSubcategory, categories, t]);

  const handleUpdateSubcategory = async () => {
    if (!updatedSubcategoryName.trim()) {
      setError(t("subcategory_name_required"));
      return;
    }

    const isWoodLike = isEditingSubcategoryWarehouseLike;

    const count = parseInt(
      editingSubcategory.count?.toString().trim() || "0",
      10
    );
    const length = parseInt(
      isWoodLike ? editingSubcategory.length?.toString().trim() || "1" : "1",
      10
    );
    const width = parseInt(
      isWoodLike ? editingSubcategory.width?.toString().trim() || "1" : "1",
      10
    );
    const height = parseInt(
      isWoodLike ? editingSubcategory.height?.toString().trim() || "1" : "1",
      10
    );
    const price_per_piece = parseFloat(
      editingSubcategory.price_per_piece?.toString().trim() || "0"
    );

    if (
      isNaN(count) ||
      isNaN(length) ||
      isNaN(width) ||
      isNaN(height) ||
      isNaN(price_per_piece)
    ) {
      setError(t("invalid_numeric_value"));
      return;
    }

    if (retailShopEnabled) {
      const missing = [];
      const hasShortDescription = ["ru", "et", "en"].some((lang) => String(shopContent[`shop_short_description_${lang}`] || "").trim());
      const hasFullDescription = ["ru", "et", "en"].some((lang) => String(shopContent[`shop_description_${lang}`] || "").trim());
      if (!hasShortDescription) missing.push(tt("short_description", "Краткое описание"));
      if (!hasFullDescription) missing.push(tt("full_description", "Полное описание"));
      const missingCustom = editableSubcategoryFields
        .filter((field) => field.is_required)
        .filter((field) => {
          const value = editingSubcategory.custom_field_values?.[field.key];
          return value === null || value === undefined || String(value).trim() === "";
        })
        .map((field) => field.label || field.key);
      missing.push(...missingCustom);
      if (missing.length) {
        setError(`${tt("shop_required_fields_missing", "Заполните обязательные поля")}: ${missing.join(", ")}`);
        return;
      }
    }

    try {
      await api.put(`/auth/subcategory/${editingSubcategory.id}`, {
        name: updatedSubcategoryName.trim(),
        count,
        length,
        width,
        height,
        price_per_piece,
        booked: -1,
        catalog_category_id: storeCatalogEnabled ? null : (editingSubcategory.catalog_category_id || null),
        custom_field_values: editingSubcategory.custom_field_values || {},
      });

      if (retailShopEnabled) {
        await api.put(`/auth/subcategory/${editingSubcategory.id}/shop-content`, { values: shopContent });
      }

      setSuccess(retailShopEnabled ? tt("product_updated_store_ready", "Товар и данные интернет-магазина сохранены") : t("subcategory_updated"));
      setEditingSubcategory(null);
      fetchSubcategories();
    } catch (error) {
      setError(error.response?.data?.detail || t("error_updating_subcategory"));
      console.error(error);
    }
  };

  const handleCategoryChange = async (event) => {
    const categoryId = event.target.value;

    setNewSubcategory((prev) => ({
      ...prev,
      categoryId,
    }));

    if (!categoryId) {
      setSubcategories([]);
      setSubcategoryFields([]);
      return;
    }

    await fetchSubcategoryFields(categoryId);

    try {
      const response = await api.get(
        `/auth/subcategories/by-category-id/${categoryId}`
      );
      setSubcategories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      if (error.response?.status === 404) {
        setSubcategories([]);
      } else {
        console.error(t("error_fetching_subcategories"), error);
      }
    }
  };

  const renderPagination = ({
    page,
    totalPages,
    onPageChange,
    showingFrom,
    showingTo,
    total,
  }) => (
    <div className="cmp-pagination-bar">
      <div className="cmp-pagination-info">
        {t("showing_entries", {
          start: showingFrom,
          end: showingTo,
          total,
        })}
      </div>

      <div className="cmp-pagination-controls">
        <button
          type="button"
          className="cmp-page-btn"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label={t("previous")}
        >
          <svg width="10" height="10" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" transform="rotate(0 0 0)">
            <path d="M15.2335 5.21967C15.5263 5.51256 15.5263 5.98744 15.2335 6.28033L9.51379 12L15.2335 17.7197C15.5263 18.0126 15.5263 18.4874 15.2335 18.7803C14.9406 19.0732 14.4657 19.0732 14.1728 18.7803L7.92279 12.5303C7.6299 12.2374 7.6299 11.7626 7.92279 11.4697L14.1728 5.21967C14.4657 4.92678 14.9406 4.92678 15.2335 5.21967Z" fill="#fff" />
          </svg>
        </button>

        <span className="cmp-page-current">{page}</span>

        <button
          type="button"
          className="cmp-page-btn"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          aria-label={t("next")}
        >
          <svg width="10" height="10" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg" transform="rotate(0 0 0)">
            <path d="M9.08979 19.2803C8.79689 18.9874 8.79689 18.5126 9.08979 18.2197L14.8095 12.5L9.08979 6.78033C8.7969 6.48744 8.7969 6.01256 9.08979 5.71967C9.38268 5.42678 9.85756 5.42678 10.1504 5.71967L16.4004 11.9697C16.6933 12.2626 16.6933 12.7374 16.4004 13.0303L10.1504 19.2803C9.85755 19.5732 9.38268 19.5732 9.08979 19.2803Z" fill="#fff"></path>
          </svg>
        </button>
      </div>
      {translationJob?.jobId ? (
        <div className="cmp-translation-lock" role="dialog" aria-modal="true" aria-live="polite">
          <div className="cmp-translation-lock-card">
            <div
              className="cmp-translation-progress-ring"
              style={{ "--translation-progress": `${Math.max(0, Math.min(100, Number(translationJob.progress || 0)))}%` }}
            >
              <div className="cmp-translation-progress-inner">
                <strong>{Math.max(0, Math.min(100, Number(translationJob.progress || 0)))}%</strong>
                <span>{tt("translation_progress", "переведено")}</span>
              </div>
            </div>
            <div className="cmp-translation-lock-copy">
              <span className="cmp-translation-lock-eyebrow">✦ {tt("automatic_translation", "Автоматический перевод")}</span>
              <h3>{tt("translation_wait_title", "Подождите, идёт перевод")}</h3>
              <p>{tt("translation_wait_hint", "Не закрывайте и не перезагружайте страницу. Если страница всё же обновится, перевод продолжится и индикатор восстановится автоматически.")}</p>
              <div className="cmp-translation-progress-line">
                <span style={{ width: `${Math.max(2, Math.min(100, Number(translationJob.progress || 0)))}%` }} />
              </div>
              <div className="cmp-translation-progress-meta">
                <span>{translationJob.sourceLang?.toUpperCase()} → {translationJob.targetLang?.toUpperCase()}</span>
                <span>
                  {Number(translationJob.total || 0) > 0
                    ? `${Math.min(Number(translationJob.current || 0), Number(translationJob.total || 0))} / ${Number(translationJob.total || 0)}`
                    : tt("translation_preparing", "Подготовка…")}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : null}

    </div>
  );

  return (
    <main className={`cmp-page ${isFullScreen ? "cmp-page-fullscreen" : ""}`}>
      <div className="cmp-page-header">
        <div>
          <div className="cmp-kicker">{t("SettingsProducts")}</div>
          <h1>{t("SettingsProducts")}</h1>
          <p>
            {tt(
              "products_settings_subtitle",
              "Manage product categories and subcategories"
            )}
          </p>
        </div>
        <button
          type="button"
          className="cmp-btn cmp-btn-primary cmp-open-orders-btn"
          onClick={() => navigate("/menu/products")}
        >
          <i className="lni lni-cart-1" aria-hidden="true"></i>
          {t("open_orders")}
        </button>
      </div>

      <section className="cmp-business-flow" aria-label={t("catalog_order_flow")}>
        <div className="cmp-flow-intro">
          <span className="cmp-flow-intro-icon">
            <i className="lni lni-layers-1" aria-hidden="true"></i>
          </span>
          <div>
            <strong>{t("catalog_order_flow")}</strong>
            <p>{t("catalog_order_flow_hint")}</p>
          </div>
        </div>

        <div className="cmp-flow-steps">
          <div className="cmp-flow-step">
            <b>1</b>
            <span>{t("flow_create_category")}</span>
          </div>
          <i className="lni lni-arrow-right cmp-flow-arrow" aria-hidden="true"></i>
          <div className="cmp-flow-step">
            <b>2</b>
            <span>{t("flow_add_product_option")}</span>
          </div>
          <i className="lni lni-arrow-right cmp-flow-arrow" aria-hidden="true"></i>
          <div className="cmp-flow-step">
            <b>3</b>
            <span>{t("flow_create_order")}</span>
          </div>
        </div>
      </section>

      {(error || success) && (
        <div
          className={`cmp-alert ${error ? "cmp-alert-error" : "cmp-alert-success"
            }`}
        >
          {error || success}
        </div>
      )}

<section className={`cmp-catalog-mode-panel ${storeCatalogEnabled ? "is-extended" : "is-classic"}`}>
        <div className="cmp-catalog-mode-main">
          <div className="cmp-catalog-mode-icon" aria-hidden="true">
            <i className="lni lni-layers-1"></i>
          </div>
          <div className="cmp-catalog-mode-copy">
            <span className="cmp-catalog-eyebrow">{t("catalog_hierarchy_badge")}</span>
            <strong>{t("catalog_hierarchy_title")}</strong>
            <span>{t("catalog_hierarchy_hint")}</span>
          </div>
        </div>

        <div className="cmp-catalog-mode-actions">
          <div className="cmp-catalog-mode-status">
            <span className={`cmp-status-dot ${storeCatalogEnabled ? "active" : ""}`}></span>
            <span>{storeCatalogEnabled ? t("catalog_hierarchy_extended_active") : t("catalog_hierarchy_classic_active")}</span>
          </div>
          <label className="cmp-switch" title={t("catalog_hierarchy_switch_title")}>
            <input
              type="checkbox"
              checked={storeCatalogEnabled}
              disabled={catalogHierarchySaving}
              onChange={(e) => handleCatalogHierarchyToggle(e.target.checked)}
              aria-label={t("catalog_hierarchy_switch_title")}
            />
            <span className="cmp-switch-slider"></span>
          </label>
        </div>

        <div className="cmp-catalog-mode-diagram" aria-label={t("catalog_hierarchy_current_structure")}>
          {storeCatalogEnabled && (
            <>
              <span className="cmp-structure-node cmp-structure-node-accent">{t("catalog_level_section")}</span>
              <i className="lni lni-arrow-right"></i>
            </>
          )}
          {storeCatalogEnabled ? (
            <>
              <span className="cmp-structure-node">{t("catalog_level_leaf_category")}</span>
              <i className="lni lni-arrow-right"></i>
              <span className="cmp-structure-node">{t("catalog_level_item")}</span>
            </>
          ) : (
            <>
              <span className="cmp-structure-node">{t("catalog_level_category")}</span>
              <i className="lni lni-arrow-right"></i>
              <span className="cmp-structure-node">{t("catalog_level_subcategory")}</span>
              <i className="lni lni-arrow-right"></i>
              <span className="cmp-structure-node">{t("catalog_level_product")}</span>
            </>
          )}
        </div>

        <div className="cmp-catalog-mode-help">
          <i className="lni lni-information"></i>
          <span>{storeCatalogEnabled ? t("catalog_hierarchy_extended_help") : t("catalog_hierarchy_classic_help")}</span>
        </div>
      </section>

{storeCatalogEnabled && (
      <section className="cmp-catalog-tree-card">
        <div className="cmp-catalog-tree-head">
          <div>
            <span className="cmp-catalog-eyebrow">{t("catalog_level_section")}</span>
            <h2>{t("catalog_sections_title")}</h2>
            <p>{t("catalog_sections_hint")}</p>
          </div>
          <span className="cmp-catalog-count">{catalogOptions.length}</span>
        </div>

        <form className="cmp-catalog-create" onSubmit={handleCreateCatalogNode}>
          <input
            className="cmp-input"
            value={catalogDraft.name}
            onChange={(e) => setCatalogDraft({ ...catalogDraft, name: e.target.value })}
            placeholder={t("catalog_new_section")}
            aria-label={t("catalog_new_section")}
          />
          <button className="cmp-btn cmp-btn-primary" type="submit" disabled={catalogBusy}>
            {catalogBusy ? t("saving") : `+ ${t("catalog_add_section")}`}
          </button>
        </form>

        <div className="cmp-catalog-language-panel">
          <div className="cmp-catalog-language-copy">
            <strong>{tt("catalog_menu_languages", "Языки меню магазина")}</strong>
            <span>{tt("catalog_menu_languages_hint", "Переведите всё дерево одним нажатием или откройте язык и исправьте любое название вручную.")}</span>
          </div>
          <div className="cmp-catalog-language-controls">
            {["ru", "et", "en"].map((lang) => (
              <div className={`cmp-catalog-lang-group ${catalogLanguage === lang ? "is-active" : ""}`} key={lang}>
                <button type="button" className="cmp-catalog-lang-tab" onClick={() => setCatalogLanguage(lang)}>
                  {lang === "ru" ? "RU · Русский" : lang === "et" ? "ET · Eesti" : "EN · English"}
                </button>
                {lang !== catalogLanguage ? (
                  <button type="button" className="cmp-catalog-translate-btn" onClick={() => autoTranslateCatalogMenu(lang)} disabled={Boolean(catalogTranslationBusy)} title={tt("translate", "Перевести")}>
                    <span>{catalogTranslationBusy === lang ? "…" : "✦"}</span>
                    {catalogTranslationBusy === lang ? tt("translating", "Перевод…") : tt("translate", "Перевести")}
                  </button>
                ) : null}
              </div>
            ))}
          </div>
          <div className="cmp-catalog-menu-search">
            <i className="lni lni-search-alt" aria-hidden="true"></i>
            <input value={catalogMenuSearch} onChange={(e) => setCatalogMenuSearch(e.target.value)} placeholder={tt("catalog_menu_search", "Найти категорию на любом языке…")} />
            {catalogMenuSearch ? <button type="button" onClick={() => setCatalogMenuSearch("")}>×</button> : null}
          </div>
        </div>

        <div className="cmp-catalog-usage-steps">
          <div><b>1</b><span><strong>{t("catalog_usage_step1_title")}</strong><small>{t("catalog_usage_step1_text")}</small></span></div>
          <i className="lni lni-arrow-right" aria-hidden="true"></i>
          <div><b>2</b><span><strong>{t("catalog_usage_step2_title")}</strong><small>{t("catalog_usage_step2_text")}</small></span></div>
          <i className="lni lni-arrow-right" aria-hidden="true"></i>
          <div><b>3</b><span><strong>{t("catalog_usage_step3_title")}</strong><small>{t("catalog_usage_step3_text")}</small></span></div>
        </div>

        <div className="cmp-catalog-toolbar">
          <button type="button" className="cmp-btn" onClick={() => setCatalogImportOpen(true)}>
            <i className="lni lni-upload-1" aria-hidden="true"></i> {t("catalog_csv_import")}
          </button>
          <button type="button" className="cmp-btn cmp-catalog-sync-btn" onClick={handleSyncCatalogLegacy} disabled={!catalogOptions.length || catalogSyncBusy}>
            <i className="lni lni-reload" aria-hidden="true"></i> {catalogSyncBusy ? t("saving") : t("catalog_sync_legacy")}
          </button>
        </div>

        {!!catalogOptions.length && !categories.length && (
          <div className="cmp-catalog-sync-notice" role="status">
            <div>
              <strong>{t("catalog_legacy_missing_title")}</strong>
              <span>{t("catalog_legacy_missing_hint")}</span>
            </div>
            <button type="button" className="cmp-btn cmp-btn-primary" onClick={handleSyncCatalogLegacy} disabled={catalogSyncBusy}>
              {catalogSyncBusy ? t("saving") : t("catalog_restore_legacy")}
            </button>
          </div>
        )}

        <div className="cmp-catalog-tree-list">
          {filteredCatalogOptions.map((node) => (
            <div className="cmp-catalog-node" key={node.id} style={{ "--tree-depth": node.depth }}>
              <span className="cmp-catalog-branch" aria-hidden="true">{node.depth ? "↳" : "●"}</span>
              <div className="cmp-catalog-node-copy">
                <strong>{getCatalogLocalizedName(node)}</strong>
                <small><span className="cmp-catalog-lang-mini">{catalogLanguage.toUpperCase()}</span>{node.product_count || 0} {t("catalog_products_count")}</small>
              </div>
              <div className="cmp-row-actions">
                <button type="button" className="cmp-icon-btn cmp-edit" onClick={() => editCatalogTranslation(node)} title={tt("edit_translation", "Изменить название на выбранном языке")}>
                  <i className="lni lni-pencil-1"></i>
                </button>
                <button type="button" className="cmp-icon-btn cmp-delete" onClick={() => handleDeleteCatalogNode(node)} title={t("delete")}>
                  <i className="lni lni-trash-3"></i>
                </button>
              </div>
            </div>
          ))}
          {!filteredCatalogOptions.length && <div className="cmp-empty">{catalogMenuSearch ? tt("catalog_menu_no_results", "Ничего не найдено") : t("catalog_tree_empty")}</div>}
        </div>
      </section>

      )}

      {catalogImportOpen && storeCatalogEnabled && (
        <div className="cmp-modal-backdrop cmp-catalog-import-backdrop" role="presentation" onMouseDown={() => !catalogImportBusy && setCatalogImportOpen(false)}>
          <div className="cmp-modal cmp-catalog-import-modal" role="dialog" aria-modal="true" aria-labelledby="catalog-import-title" onMouseDown={(e) => e.stopPropagation()}>
            <div className="cmp-catalog-import-head">
              <div className="cmp-catalog-import-heading">
                <div className="cmp-catalog-import-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="M12 3v11m0 0 4-4m-4 4-4-4M5 15.5v2.25A2.25 2.25 0 0 0 7.25 20h9.5A2.25 2.25 0 0 0 19 17.75V15.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg></div>
                <div><span className="cmp-catalog-eyebrow">{t("store_catalog_tree")}</span><h3 id="catalog-import-title">{t("catalog_csv_import")}</h3><p>{t("catalog_csv_import_hint")}</p></div>
              </div>
              <button type="button" className="cmp-modal-close cmp-catalog-import-close" onClick={() => setCatalogImportOpen(false)} disabled={catalogImportBusy} aria-label={t("close")}>×</button>
            </div>
            <div className="cmp-modal-body cmp-catalog-import-body">
              <div className="cmp-catalog-import-section">
                <div className="cmp-catalog-import-section-title"><span>01</span><div><strong>{t("catalog_csv_file_label")}</strong><small>{t("catalog_csv_file_help")}</small></div></div>
                <label className={`cmp-catalog-dropzone ${catalogImportFile ? "has-file" : ""}`}>
                  <input id="catalog-csv-input" type="file" accept=".csv,text/csv" onChange={(e) => setCatalogImportFile(e.target.files?.[0] || null)} />
                  <div className="cmp-catalog-dropzone-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none"><path d="M12 16V7m0 0 3.5 3.5M12 7 8.5 10.5M5 15.5v2A2.5 2.5 0 0 0 7.5 20h9a2.5 2.5 0 0 0 2.5-2.5v-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg></div>
                  <div className="cmp-catalog-dropzone-copy"><strong>{catalogImportFile ? catalogImportFile.name : t("catalog_csv_choose_file")}</strong><span>{catalogImportFile ? t("catalog_csv_file_ready") : t("catalog_csv_drop_hint")}</span></div>
                  <span className="cmp-catalog-browse-btn">{t("catalog_csv_browse")}</span>
                </label>
              </div>
              <div className="cmp-catalog-import-section">
                <div className="cmp-catalog-import-section-title"><span>02</span><div><strong>{t("catalog_csv_mode_label")}</strong><small>{t("catalog_csv_mode_help")}</small></div></div>
                <div className="cmp-catalog-mode-grid" role="radiogroup" aria-label={t("catalog_csv_mode_label")}>
                  <button type="button" role="radio" aria-checked={catalogImportMode === "merge"} className={`cmp-catalog-mode-card ${catalogImportMode === "merge" ? "active" : ""}`} onClick={() => setCatalogImportMode("merge")}><span className="cmp-catalog-mode-check">✓</span><strong>{t("catalog_csv_mode_merge")}</strong><small>{t("catalog_csv_mode_merge_help")}</small></button>
                  <button type="button" role="radio" aria-checked={catalogImportMode === "replace"} className={`cmp-catalog-mode-card cmp-catalog-mode-danger ${catalogImportMode === "replace" ? "active" : ""}`} onClick={() => setCatalogImportMode("replace")}><span className="cmp-catalog-mode-check">✓</span><strong>{t("catalog_csv_mode_replace")}</strong><small>{t("catalog_csv_mode_replace_help")}</small></button>
                </div>
              </div>
              <div className="cmp-catalog-import-note"><svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 8v4m0 4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg><div><strong>{t("catalog_csv_tenant_safe_title")}</strong><span>{t("catalog_csv_tenant_safe_text")}</span></div><button type="button" onClick={handleDownloadCatalogTemplate}>{t("catalog_csv_download_template")}</button></div>
            </div>
            <div className="cmp-modal-foot cmp-catalog-import-foot"><button type="button" className="cmp-btn cmp-catalog-cancel" onClick={() => setCatalogImportOpen(false)} disabled={catalogImportBusy}>{t("cancel")}</button><button type="button" className="cmp-btn cmp-btn-primary cmp-catalog-import-submit" onClick={handleImportCatalogCsv} disabled={!catalogImportFile || catalogImportBusy}><svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 16V7m0 0 3.5 3.5M12 7 8.5 10.5M5 15.5v2A2.5 2.5 0 0 0 7.5 20h9a2.5 2.5 0 0 0 2.5-2.5v-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>{catalogImportBusy ? t("saving") : t("catalog_csv_import_button")}</button></div>
          </div>
        </div>
      )}

      {storeCatalogEnabled && catalogRootSections.length > 0 && (
        <section className="cmp-store-sections-strip" aria-label={tt("store_departments", "Store departments")}>
          <div className="cmp-store-sections-copy">
            <span className="cmp-catalog-eyebrow">{tt("store_departments", "Store departments")}</span>
            <strong>{tt("store_departments_hint", "Top level. Departments are not product categories and are shown only once.")}</strong>
          </div>
          <div className="cmp-store-section-chips">
            {catalogRootSections.map((section) => (
              <span className="cmp-store-section-chip" key={section.id}>
                <i className="lni lni-layers-1" aria-hidden="true"></i>
                {getCatalogLocalizedName(section)}
              </span>
            ))}
          </div>
        </section>
      )}

      <div className="cmp-layout">
        <section className="cmp-card cmp-card-categories">
          <div className="cmp-card-head">
            <div className="cmp-head-icon cmp-head-icon-blue">
              <svg
                width="30"
                height="30"
                viewBox="0 0 24 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.43066 4.25H4.25C3.00736 4.25 2 5.25736 2 6.5V18.5C2 19.7426 3.00736 20.75 4.25 20.75H19.75C20.9926 20.75 22 19.7426 22 18.5V7.75H12.3185C12.0633 7.75 11.8256 7.62023 11.6876 7.40557L10.3233 5.2833C9.90932 4.63929 9.19626 4.25 8.43066 4.25Z"
                  fill="#fff"
                />
                <path
                  opacity="0.4"
                  d="M21.9872 6.25C21.8629 5.12501 20.9091 4.25 19.7509 4.25H11.4316C11.4855 4.32186 11.537 4.39594 11.586 4.47216L12.7289 6.25H21.9872Z"
                  fill="#fff"
                />
              </svg>
            </div>

            <div>
              <h2>{t("manage_categories")}</h2>
              <p>
                {tt(
                  "create_manage_product_categories",
                  "Create and manage product categories"
                )}
              </p>
            </div>
          </div>

          {!storeCatalogEnabled ? (
            <form onSubmit={handleCreateCategory} className="cmp-create-category">
              <input
                type="text"
                className="cmp-input"
                placeholder={t("new_category")}
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                required
              />
              <button type="submit" className="cmp-btn cmp-btn-primary">
                + {t("add_category")}
              </button>
            </form>
          ) : (
            <div className="cmp-inline-guidance">
              <i className="lni lni-information"></i>
              <span>{t("catalog_leaf_categories_managed_hint")}</span>
            </div>
          )}

          <div className="cmp-list-toolbar cmp-category-toolbar">
            <div className="cmp-category-toolbar-top">
              <div className="cmp-list-title">
                <span>{t("category")}</span>
                <b>{filteredCategories.length}</b>
              </div>

              {categories.length > 0 && (
                <button
                  type="button"
                  className="cmp-btn cmp-btn-danger-ghost cmp-clear-categories-btn"
                  onClick={handleDeleteAllCategories}
                  title={tt("clear_category_list_hint", "Delete all categories from this list")}
                >
                  <i className="lni lni-trash-3"></i>
                  {tt("clear_category_list", "Clear list")}
                </button>
              )}
            </div>

            <div className="cmp-category-search-wrap">
              <i className="lni lni-search-alt" aria-hidden="true"></i>
              <input
                type="text"
                className="cmp-search-input"
                placeholder={t("search")}
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
              />
            </div>
          </div>

          <div className="cmp-category-list">
            {paginatedCategories.map((category) => (
              <div className="cmp-category-row" key={category.id}>
                <div className="cmp-category-info">
                  <span className="cmp-category-icon">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M2.31641 3.25C1.90219 3.25 1.56641 3.58579 1.56641 4C1.56641 4.41421 1.90219 4.75 2.31641 4.75H3.49696C3.87082 4.75 4.18759 5.02534 4.23965 5.39556L5.49371 14.3133C5.6499 15.424 6.60021 16.25 7.72179 16.25L18.0664 16.25C18.4806 16.25 18.8164 15.9142 18.8164 15.5C18.8164 15.0858 18.4806 14.75 18.0664 14.75L7.72179 14.75C7.34793 14.75 7.03116 14.4747 6.9791 14.1044L6.85901 13.2505H17.7114C18.6969 13.2505 19.5678 12.6091 19.8601 11.668L21.7824 5.48032C21.8531 5.25268 21.8114 5.00499 21.6701 4.81305C21.5287 4.62112 21.3045 4.50781 21.0662 4.50781H5.51677C5.14728 3.75572 4.37455 3.25 3.49696 3.25H2.31641ZM5.84051 6.00781L6.64807 11.7505H17.7114C18.0399 11.7505 18.3302 11.5367 18.4277 11.223L20.0478 6.00781H5.84051Z"
                        fill="#fff"
                      />
                      <path
                        d="M7.78418 17.75C6.81768 17.75 6.03418 18.5335 6.03418 19.5C6.03418 20.4665 6.81768 21.25 7.78418 21.25C8.75068 21.25 9.53428 20.4665 9.53428 19.5C9.53428 18.5335 8.75068 17.75 7.78418 17.75Z"
                        fill="#fff"
                      />
                      <path
                        d="M14.5703 19.5C14.5703 18.5335 15.3538 17.75 16.3203 17.75C17.2868 17.75 18.0704 18.5335 18.0704 19.5C18.0704 20.4665 17.2869 21.25 16.3204 21.25C15.3539 21.25 14.5703 20.4665 14.5703 19.5Z"
                        fill="#fff"
                      />
                    </svg>
                  </span>

                  <div className="cmp-category-name-wrap">
                    <strong title={getCategoryName(category)}>
                      {getCategoryName(category)}
                    </strong>
                    {getCatalogSectionForCategory(category) && (
                      <small className="cmp-category-section-label">
                        {getCatalogSectionForCategory(category).name}
                      </small>
                    )}
                  </div>
                </div>

                <div className="cmp-row-actions">
                  <button
                    type="button"
                    className="cmp-icon-btn cmp-edit"
                    onClick={() => handleEditCategory(category)}
                    title={t("edit")}
                  >
                    <i className="lni lni-pencil-1"></i>
                  </button>

                  <button
                    type="button"
                    className="cmp-icon-btn cmp-fields"
                    onClick={() => handleOpenFieldManager(category)}
                    title={tt("manage_fields", "Manage fields")}
                  >
                    <i className="lni lni-layout-9"></i>
                  </button>

                  <button
                    type="button"
                    className="cmp-icon-btn cmp-delete"
                    onClick={() => handleDeleteCategory(category.id)}
                    title={t("delete")}
                  >
                    <i className="lni lni-trash-3"></i>
                  </button>
                </div>
              </div>
            ))}

            {filteredCategories.length === 0 && (
              <div className="cmp-empty">
                {tt("no_categories_found", "No categories found")}
              </div>
            )}
          </div>

          {renderPagination({
            page: categoryPage,
            totalPages: categoryTotalPages,
            onPageChange: setCategoryPage,
            showingFrom: categoryShowingFrom,
            showingTo: categoryShowingTo,
            total: filteredCategories.length,
          })}
        </section>

        <section className="cmp-card cmp-card-subcategories">
          <div className="cmp-card-head cmp-card-head-between">
            <div className="cmp-title-row">
              <div className="cmp-head-icon cmp-head-icon-green">
                <svg
                  width="30"
                  height="30"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.5 2.75C3.5 2.33579 3.16421 2 2.75 2C2.33579 2 2 2.33579 2 2.75V17.25C2 18.4926 3.00736 19.5 4.25 19.5H8.69C9.10421 19.5 9.44 19.1642 9.44 18.75C9.44 18.3358 9.10421 18 8.69 18H4.25C3.83579 18 3.5 17.6642 3.5 17.25V8.75H8.69C9.10421 8.75 9.44 8.41421 9.44 8C9.44 7.58579 9.10421 7.25 8.69 7.25H3.5V2.75Z"
                    fill="#fff"
                  />
                  <path
                    d="M12.6164 2C11.6913 2 10.9414 2.74994 10.9414 3.67504V9.57497C10.9414 10.5001 11.6913 11.25 12.6164 11.25H20.3247C21.2498 11.25 21.9997 10.5001 21.9997 9.57497V5.22504C21.9997 4.29994 21.2498 3.55 20.3247 3.55H16.6736C16.6185 3.55 16.5666 3.52406 16.5336 3.47999L15.9261 2.67001C15.6097 2.24823 15.1133 2 14.5861 2H12.6164Z"
                    fill="#fff"
                  />
                  <path
                    d="M12.6164 12.75C11.6913 12.75 10.9414 13.4999 10.9414 14.425V20.325C10.9414 21.2501 11.6913 22 12.6164 22H20.3247C21.2498 22 21.9997 21.2501 21.9997 20.325V15.975C21.9997 15.0499 21.2498 14.3 20.3247 14.3H16.6736C16.6185 14.3 16.5666 14.2741 16.5336 14.23L15.9261 13.42C15.6097 12.9982 15.1133 12.75 14.5861 12.75H12.6164Z"
                    fill="#fff"
                  />
                </svg>
              </div>

              <div>
                <h2>{storeCatalogEnabled ? t("manage_catalog_items") : t("manage_subcategories")}</h2>
                <p>
                  {selectedCategoryUsesWarehouseFields
                    ? tt(
                      "set_dimensions_prices_availability",
                      "Set dimensions, prices and availability"
                    )
                    : tt(
                      "set_business_options_availability",
                      "Set business options and prices for the selected category"
                    )}
                </p>
              </div>
            </div>

            <button
              type="button"
              className="cmp-btn cmp-btn-soft"
              onClick={() => setIsFullScreen(!isFullScreen)}
            >
              {isFullScreen ? "✕" : "⛶"}{" "}
              {isFullScreen ? t("exit_fullscreen") : t("fullscreen")}
            </button>
          </div>

          <form
            onSubmit={handleCreateSubcategory}
            className={`cmp-sub-form ${
              selectedCategoryUsesWarehouseFields
                ? "cmp-sub-form-warehouse"
                : "cmp-sub-form-simple"
            }`}
          >
            <div className="cmp-field cmp-field-name">
              <label>{storeCatalogEnabled ? t("catalog_item") : t("subcategory")}</label>
              <input
                type="text"
                className="cmp-input"
                placeholder={storeCatalogEnabled ? t("new_catalog_item") : t("new_subcategory")}
                value={newSubcategory.name}
                onChange={(e) =>
                  setNewSubcategory({ ...newSubcategory, name: e.target.value })
                }
                required
              />
            </div>

            {selectedCategoryUsesWarehouseFields && (
              <>
                <div className="cmp-field">
                  <label>{t("length")}</label>
                  <input
                    type="number"
                    className="cmp-input"
                    placeholder={t("length")}
                    value={newSubcategory.length}
                    onChange={(e) =>
                      setNewSubcategory({
                        ...newSubcategory,
                        length: e.target.value,
                      })
                    }
                    required
                  />
                </div>

                <div className="cmp-field">
                  <label>{t("width")}</label>
                  <input
                    type="number"
                    className="cmp-input"
                    placeholder={t("width")}
                    value={newSubcategory.width}
                    onChange={(e) =>
                      setNewSubcategory({
                        ...newSubcategory,
                        width: e.target.value,
                      })
                    }
                    required
                  />
                </div>

                <div className="cmp-field">
                  <label>{t("height")}</label>
                  <input
                    type="number"
                    className="cmp-input"
                    placeholder={t("height")}
                    value={newSubcategory.height}
                    onChange={(e) =>
                      setNewSubcategory({
                        ...newSubcategory,
                        height: e.target.value,
                      })
                    }
                    required
                  />
                </div>
              </>
            )}

            <div className="cmp-field">
              <label>{t("price_per_piece")}</label>
              <input
                type="number"
                className="cmp-input"
                placeholder={t("price_per_piece")}
                value={newSubcategory.price_per_piece}
                onChange={(e) =>
                  setNewSubcategory({
                    ...newSubcategory,
                    price_per_piece: e.target.value,
                  })
                }
                required
              />
            </div>

            <div className="cmp-field">
              <label>{t("count")}</label>
              <input
                type="number"
                className="cmp-input"
                placeholder={t("new_subcategory_count")}
                value={newSubcategory.count}
                onChange={(e) =>
                  setNewSubcategory({
                    ...newSubcategory,
                    count: e.target.value,
                  })
                }
                min="0"
                required
              />
            </div>

            {!retailShopEnabled && visibleSubcategoryFields.map((field) => (
              <div className="cmp-field" key={field.id}>
                <label>{field.label}</label>
                {renderCustomFieldInput(
                  field,
                  newSubcategory.custom_field_values?.[field.key],
                  (value) => updateNewSubcategoryCustomValue(field.key, value)
                )}
              </div>
            ))}

            <div className="cmp-field cmp-field-category" style={{ display: storeCatalogEnabled ? "none" : undefined }}>
              <label>{t("store_catalog_section")}</label>
              <select
                className="cmp-input cmp-select"
                value={newSubcategory.catalogCategoryId}
                onChange={(e) => setNewSubcategory({ ...newSubcategory, catalogCategoryId: e.target.value })}
              >
                <option value="">{t("catalog_not_assigned")}</option>
                {catalogOptions.map((node) => (
                  <option key={node.id} value={node.id}>{`${"— ".repeat(node.depth)}${node.name}`}</option>
                ))}
              </select>
            </div>

            <div className="cmp-field cmp-field-category">
              <label>{t("select_category")}</label>
              <CategorySearchSelect
                value={newSubcategory.categoryId}
                categories={categories}
                getCategoryName={getCategoryName}
                onChange={handleCategoryChange}
                placeholder={t("select_category")}
                searchPlaceholder={`${t("search")}...`}
              />
            </div>

            {retailShopEnabled && (
              <div className="cmp-store-required-hint" title={tt("store_fields_after_basic", "После основных данных откроются обязательные поля интернет-магазина")}>
                <i className="lni lni-store"></i>
                <span>{tt("store_data_next", "Данные магазина")}</span>
                <b>3</b>
              </div>
            )}

            <button
              type="submit"
              className="cmp-btn cmp-btn-primary cmp-add-sub-btn"
              disabled={isCreatingSubcategory}
            >
              {isCreatingSubcategory ? tt("saving", "Saving…") : `+ ${storeCatalogEnabled ? t("add_catalog_item") : t("add_subcategory")}`}
            </button>
          </form>

          <div className="cmp-list-toolbar cmp-sub-toolbar">
            <div className="cmp-list-title">
              <span>{storeCatalogEnabled ? t("manage_catalog_items") : t("manage_subcategories")}</span>
              <b>{filteredSubcategories.length}</b>
              {selectedCategoryName && <em>{selectedCategoryName}</em>}
            </div>

            <div className="cmp-toolbar-actions">
              {subcategories.length > 0 && (
                <button type="button" className="cmp-btn cmp-btn-danger-ghost" onClick={handleDeleteAllSubcategories}>
                  <i className="lni lni-trash-3"></i>
                  {tt("delete_all", "Delete all")}
                </button>
              )}
              <input
                type="text"
                className="cmp-search-input"
                placeholder={t("search")}
                value={subcategorySearch}
                onChange={(e) => setSubcategorySearch(e.target.value)}
              />
            </div>
          </div>

          <div className="cmp-table-shell">
            <table className="cmp-table">
              <thead>
                <tr>
                  <th>{storeCatalogEnabled ? t("catalog_item") : t("subcategory")}</th>

                  {selectedCategoryUsesWarehouseFields && (
                    <>
                      <th>{t("length")}</th>
                      <th>{t("width")}</th>
                      <th>{t("height")}</th>
                    </>
                  )}

                  <th>{t("price_per_piece")}</th>
                  <th>{t("count")}</th>
                  <th>{t("booked")}</th>
                  <th>{t("balance")}</th>

                  {visibleSubcategoryFields.map((field) => (
                    <th key={field.id}>{field.label}</th>
                  ))}

                  {selectedCategoryUsesWarehouseFields && (
                    <th>{t("m3")}</th>
                  )}

                  <th>{t("actions")}</th>
                </tr>
              </thead>

              <tbody>
                {paginatedSubcategories.map((subcategory) => (
                  <tr key={subcategory.id}>
                    <td>
                      <div className="cmp-product-cell">
                        <button
                          type="button"
                          className={`cmp-product-thumb-trigger${subcategory.image_attachment_id ? " has-photo" : ""}`}
                          onClick={() => openPhotoManager(subcategory)}
                          title={subcategory.image_attachment_id
                            ? tt("manage_product_photos", "Open photo gallery")
                            : tt("add_product_photos", "Add photos")}
                          aria-label={subcategory.image_attachment_id
                            ? tt("manage_product_photos", "Open photo gallery")
                            : tt("add_product_photos", "Add photos")}
                        >
                          {subcategory.image_attachment_id ? (
                            <img className="cmp-product-thumb" src={productImageUrl(subcategory)} alt="" />
                          ) : (
                            <span className="cmp-product-thumb cmp-product-thumb-empty"><i className="lni lni-upload-1"></i></span>
                          )}
                          <span className="cmp-product-thumb-hover-icon" aria-hidden="true">
                            <i className="lni lni-upload-1"></i>
                          </span>
                        </button>
                        <strong>{getSubcategoryName(subcategory)}</strong>
                      </div>
                    </td>

                    {selectedCategoryUsesWarehouseFields && (
                      <>
                        <td>{subcategory.length}</td>
                        <td>{subcategory.width}</td>
                        <td>{subcategory.height}</td>
                      </>
                    )}

                    <td>{subcategory.price_per_piece}</td>
                    <td>{subcategory.count}</td>
                    <td>{subcategory.booked || 0}</td>
                    <td>{getSubcategoryBalance(subcategory)}</td>

                    {visibleSubcategoryFields.map((field) => (
                      <td key={field.id}>{getSubcategoryCustomValue(subcategory, field)}</td>
                    ))}

                    {selectedCategoryUsesWarehouseFields && (
                      <td>{subcategory.m3}</td>
                    )}

                    <td>
                      <div className="cmp-row-actions cmp-table-actions">
                        <button
                          type="button"
                          className="cmp-icon-btn cmp-content"
                          onClick={() => openContentManager(subcategory)}
                          title={tt("shop_product_content", "Shop product content")}
                        >
                          <i className="lni lni-text-format"></i>
                        </button>

                        <button
                          type="button"
                          className="cmp-icon-btn cmp-edit"
                          onClick={() => handleEditSubcategory(subcategory)}
                          title={t("edit")}
                        >
                          <i className="lni lni-pencil-1"></i>
                        </button>

                        <button
                          type="button"
                          className="cmp-icon-btn cmp-delete"
                          onClick={() => handleDeleteSubcategory(subcategory.id)}
                          title={t("delete")}
                        >
                          <i className="lni lni-trash-3"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {filteredSubcategories.length === 0 && (
                  <tr>
                    <td colSpan={selectedCategoryUsesWarehouseFields ? 11 + visibleSubcategoryFields.length : 6 + visibleSubcategoryFields.length}>
                      <div className="cmp-empty">
                        {storeCatalogEnabled ? t("no_catalog_items_found") : tt("no_subcategories_found", "No subcategories found")}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {renderPagination({
            page: subcategoryPage,
            totalPages: subcategoryTotalPages,
            onPageChange: setSubcategoryPage,
            showingFrom: subcategoryShowingFrom,
            showingTo: subcategoryShowingTo,
            total: filteredSubcategories.length,
          })}
        </section>
      </div>

      {createShopModalOpen && (
        <div className="cmp-modal-backdrop cmp-create-shop-backdrop" onMouseDown={(event) => {
          if (event.target === event.currentTarget && !isCreatingSubcategory) setCreateShopModalOpen(false);
        }}>
          <div className="cmp-modal cmp-create-shop-modal" role="dialog" aria-modal="true" aria-labelledby="create-shop-title">
            <div className="cmp-create-shop-head">
              <div className="cmp-create-shop-icon"><i className="lni lni-store"></i></div>
              <div>
                <span className="cmp-content-eyebrow">{tt("online_store", "Интернет-магазин")}</span>
                <h3 id="create-shop-title">{tt("complete_product_card", "Заполните карточку товара")}</h3>
                <p>{tt("complete_product_card_hint", "Основные данные уже готовы. Добавьте обязательную информацию, которая будет показана покупателю в VelShop.")}</p>
              </div>
              <button type="button" className="cmp-modal-close" disabled={isCreatingSubcategory} onClick={() => setCreateShopModalOpen(false)}>×</button>
            </div>

            <div className="cmp-create-shop-body">
              <div className="cmp-create-required-title">
                <div>
                  <strong>{tt("basic_product_data", "Основные данные товара")}</strong>
                  <span>{tt("basic_product_data_hint", "Заполните карточку целиком в одном окне. Поля со звёздочкой обязательны.")}</span>
                </div>
                <span className="cmp-required-count">{tt("one_product_card", "1 карточка")}</span>
              </div>

              <div className="cmp-create-shop-grid cmp-create-basic-grid">
                <div className="cmp-create-shop-field cmp-create-shop-field-wide">
                  <label>{tt("product", "Название товара")} <b>*</b></label>
                  <input autoFocus value={newSubcategory.name || ""} onChange={(e) => setNewSubcategory((prev) => ({ ...prev, name: e.target.value }))} placeholder={tt("new_product", "Новый товар")} />
                </div>
                <div className="cmp-create-shop-field">
                  <label>{tt("price_per_piece", "Цена за штуку")} <b>*</b></label>
                  <input type="number" min="0" step="0.01" value={newSubcategory.price_per_piece || ""} onChange={(e) => setNewSubcategory((prev) => ({ ...prev, price_per_piece: e.target.value }))} placeholder="0.00" />
                </div>
                <div className="cmp-create-shop-field">
                  <label>{tt("count", "Количество")} <b>*</b></label>
                  <input type="number" min="0" value={newSubcategory.count || ""} onChange={(e) => setNewSubcategory((prev) => ({ ...prev, count: e.target.value }))} placeholder="0" />
                </div>
                <div className="cmp-create-shop-field cmp-create-shop-field-wide">
                  <label>{tt("category", "Категория")} <b>*</b></label>
                  <CategorySearchSelect
                    value={newSubcategory.categoryId || ""}
                    categories={categories}
                    getCategoryName={getCategoryName}
                    onChange={handleCategoryChange}
                    placeholder={tt("select_category", "Выберите категорию")}
                    searchPlaceholder={`${tt("search", "Поиск")}...`}
                  />
                </div>
              </div>

              <div className="cmp-create-section-divider"></div>
              <div className="cmp-create-required-title">
                <div>
                  <strong>{tt("required_store_fields", "Обязательные поля")}</strong>
                  <span>{tt("required_store_fields_hint", "Без них товар нельзя корректно опубликовать в интернет-магазине")}</span>
                </div>
                <span className="cmp-required-count">3 {tt("required_short", "обязательных")}</span>
              </div>

              <div className="cmp-create-shop-grid">
                <div className="cmp-create-shop-field">
                  <label>{tt("shop_sku", "SKU / Артикул")} <span className="cmp-auto-badge">AUTO</span></label>
                  <input autoFocus value={shopContent.shop_sku || ""} onChange={(e) => updateShopContent("shop_sku", e.target.value)} placeholder={tt("shop_sku_auto_placeholder", "Создастся автоматически после добавления товара")} />
                  <small>{tt("shop_sku_hint_auto", "Можно оставить пустым. Система создаст уникальный SKU; свой код можно вписать вручную.")}</small>
                </div>

                <div className="cmp-create-shop-field cmp-create-shop-field-wide">
                  <label>{tt("short_description", "Краткое описание")} <b>*</b></label>
                  <textarea rows="3" value={shopContent.shop_short_description_ru || ""} onChange={(e) => updateShopContent("shop_short_description_ru", e.target.value)} placeholder={tt("short_description_placeholder_ru", "1–2 предложения: что это за товар и для чего он нужен")}></textarea>
                  <small>{tt("short_description_shop_hint", "Показывается в карточке рядом с названием и ценой")}</small>
                </div>

                <div className="cmp-create-shop-field cmp-create-shop-field-wide">
                  <label>{tt("full_description", "Полное описание")} <b>*</b></label>
                  <textarea rows="6" value={shopContent.shop_description_ru || ""} onChange={(e) => updateShopContent("shop_description_ru", e.target.value)} placeholder={tt("full_description_placeholder_ru", "Подробно опишите свойства, применение, состав или другие важные особенности товара")}></textarea>
                  <small>{tt("full_description_shop_hint", "Показывается на странице товара в интернет-магазине")}</small>
                </div>
              </div>

              {editableSubcategoryFields.length > 0 && (
                <>
                  <div className="cmp-create-section-divider"></div>
                  <div className="cmp-create-required-title">
                    <div>
                      <strong>{tt("product_extra_fields", "Дополнительные поля товара")}</strong>
                      <span>{tt("product_extra_fields_hint", "Поля из настроек выбранной категории. Обязательные отмечены звёздочкой.")}</span>
                    </div>
                    <span className="cmp-required-count">{editableSubcategoryFields.length}</span>
                  </div>
                  <div className="cmp-create-shop-grid">
                    {editableSubcategoryFields.map((field) => (
                      <div className="cmp-create-shop-field" key={field.id}>
                        <label>{field.label}{field.is_required ? <> <b>*</b></> : null}</label>
                        {renderCustomFieldInput(
                          field,
                          newSubcategory.custom_field_values?.[field.key],
                          (value) => updateNewSubcategoryCustomValue(field.key, value)
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div className="cmp-create-publish-row">
                <div>
                  <strong>{tt("publish_in_store", "Показывать в интернет-магазине")}</strong>
                  <span>{tt("publish_in_store_hint", "Можно выключить и закончить оформление товара позже")}</span>
                </div>
                <label className="cmp-shop-publish-toggle">
                  <input type="checkbox" checked={shopContent.shop_enabled !== false && shopContent.shop_enabled !== "false"} onChange={(e) => updateShopContent("shop_enabled", e.target.checked)} />
                  <span>{shopContent.shop_enabled !== false && shopContent.shop_enabled !== "false" ? tt("published", "Опубликован") : tt("draft", "Черновик")}</span>
                </label>
              </div>

              <button type="button" className={`cmp-create-advanced-toggle ${createShopAdvancedOpen ? "open" : ""}`} onClick={() => setCreateShopAdvancedOpen((value) => !value)}>
                <span><i className="lni lni-cog"></i>{tt("optional_store_fields", "Дополнительные данные магазина")}</span>
                <i className="lni lni-chevron-down"></i>
              </button>

              {createShopAdvancedOpen && (
                <div className="cmp-create-advanced-panel">
                  <div className="cmp-create-shop-field cmp-create-shop-field-wide">
                    <label>{tt("short_description_en", "Краткое описание (EN)")}</label>
                    <textarea rows="2" value={shopContent.shop_short_description_en || ""} onChange={(e) => updateShopContent("shop_short_description_en", e.target.value)} />
                  </div>
                  <div className="cmp-create-shop-field cmp-create-shop-field-wide">
                    <label>{tt("full_description_en", "Полное описание (EN)")}</label>
                    <textarea rows="4" value={shopContent.shop_description_en || ""} onChange={(e) => updateShopContent("shop_description_en", e.target.value)} />
                  </div>
                  <div className="cmp-create-shop-field">
                    <label>{tt("seo_title", "SEO title")}</label>
                    <input value={shopContent.shop_seo_title_ru || ""} onChange={(e) => updateShopContent("shop_seo_title_ru", e.target.value)} />
                  </div>
                  <div className="cmp-create-shop-field">
                    <label>{tt("video_url", "Видео URL")}</label>
                    <input value={shopContent.shop_video_url || ""} onChange={(e) => updateShopContent("shop_video_url", e.target.value)} placeholder="https://..." />
                  </div>
                </div>
              )}
            </div>

            <div className="cmp-create-shop-foot">
              <div className="cmp-create-shop-note"><i className="lni lni-image"></i><span>{tt("photos_after_create", "Фотографии можно добавить сразу после создания товара через кнопку галереи.")}</span></div>
              <div className="cmp-create-shop-actions">
                <button type="button" className="cmp-btn cmp-btn-soft" disabled={isCreatingSubcategory} onClick={() => setCreateShopModalOpen(false)}>{t("cancel")}</button>
                <button type="button" className="cmp-btn cmp-btn-primary" disabled={isCreatingSubcategory} onClick={handleConfirmCreateShopProduct}>
                  {isCreatingSubcategory ? tt("saving", "Сохранение…") : tt("create_product", "Создать товар")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {photoManagerSubcategory && (
        <div className="cmp-modal-backdrop cmp-photo-modal-backdrop" onMouseDown={(event) => {
          if (event.target === event.currentTarget) closePhotoManager();
        }}>
          <div className="cmp-modal cmp-photo-manager" role="dialog" aria-modal="true">
            <div className="cmp-photo-manager-head">
              <div>
                <span className="cmp-photo-manager-kicker">{tt("product_gallery", "Product gallery")}</span>
                <h3>{getSubcategoryName(photoManagerSubcategory)}</h3>
                <p>{tt("product_gallery_description", "Manage the photos shown in the online store")}</p>
              </div>
              <button type="button" className="cmp-modal-close" onClick={closePhotoManager} aria-label={t("close")}>
                <i className="lni lni-close"></i>
              </button>
            </div>

            <div className="cmp-photo-manager-body">
              <section className="cmp-gallery-stage">
                {isLoadingPhotos ? (
                  <div className="cmp-gallery-state"><i className="lni lni-spinner-arrow"></i><span>{tt("loading_photos", "Loading photos…")}</span></div>
                ) : productPhotos.length ? (
                  <>
                    <div className="cmp-gallery-main">
                      <div className="cmp-gallery-toolbar">
                        {productPhotos[activePhotoIndex]?.web_view_link && (
                          <a href={productPhotos[activePhotoIndex].web_view_link} target="_blank" rel="noreferrer">
                            {tt("open_drive", "Open Drive")}
                          </a>
                        )}
                        <button type="button" disabled={downloadingProductPhotoId === productPhotos[activePhotoIndex]?.id} onClick={() => downloadProductPhoto(productPhotos[activePhotoIndex])}>
                          {downloadingProductPhotoId === productPhotos[activePhotoIndex]?.id ? "…" : tt("download", "Download")}
                        </button>
                        <button type="button" disabled={renamingProductPhotoId === productPhotos[activePhotoIndex]?.id} onClick={() => renameProductPhoto(productPhotos[activePhotoIndex])}>
                          {renamingProductPhotoId === productPhotos[activePhotoIndex]?.id ? "…" : tt("rename_photo", "Rename photo")}
                        </button>
                        <button type="button" className="danger" onClick={() => deleteProductPhoto(productPhotos[activePhotoIndex])}>
                          {t("delete")}
                        </button>
                      </div>
                      {productPhotos[activePhotoIndex]?.preview_url ? (
                        <img src={productPhotos[activePhotoIndex].preview_url} alt={productPhotos[activePhotoIndex].file_name || ""} />
                      ) : (
                        <div className="cmp-gallery-state"><i className="lni lni-upload-1"></i></div>
                      )}
                      {productPhotos.length > 1 && (
                        <>
                          <button type="button" className="cmp-gallery-nav prev" onClick={() => setActivePhotoIndex((activePhotoIndex - 1 + productPhotos.length) % productPhotos.length)} aria-label={tt("previous_photo", "Previous photo")}>
                            <i className="lni lni-chevron-left"></i>
                          </button>
                          <button type="button" className="cmp-gallery-nav next" onClick={() => setActivePhotoIndex((activePhotoIndex + 1) % productPhotos.length)} aria-label={tt("next_photo", "Next photo")}>
                            <i className="lni lni-chevron-right"></i>
                          </button>
                        </>
                      )}
                      <div className="cmp-gallery-counter">{activePhotoIndex + 1} / {productPhotos.length}</div>
                    </div>
                    <div className="cmp-gallery-thumbs" aria-label={tt("product_photos", "Product photos")}>
                      {productPhotos.map((photo, index) => (
                        <div className="cmp-gallery-thumb-wrap" key={photo.id}>
                          <button type="button" className={`cmp-gallery-thumb ${index === activePhotoIndex ? "active" : ""}`} onClick={() => setActivePhotoIndex(index)}>
                            {photo.preview_url ? <img src={photo.preview_url} alt="" /> : <i className="lni lni-upload-1"></i>}
                          </button>
                          <button type="button" className="cmp-gallery-thumb-delete" onClick={(event) => { event.stopPropagation(); deleteProductPhoto(photo); }} aria-label={tt("delete_photo", "Delete photo")}>
                            <i className="lni lni-trash-3"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="cmp-gallery-empty">
                    <span><i className="lni lni-upload-1"></i></span>
                    <strong>{tt("no_product_photos", "No photos yet")}</strong>
                    <p>{tt("no_product_photos_hint", "Add clear product photos so customers can see the item before ordering.")}</p>
                  </div>
                )}
              </section>

              <aside className="cmp-gallery-upload-panel">
                <div className="cmp-gallery-upload-title">
                  <span><i className="lni lni-upload-1"></i></span>
                  <div><strong>{tt("add_product_photos", "Add photos")}</strong><small>{tt("product_photo_hint", "JPG, PNG or WEBP · max 15 MB each")}</small></div>
                </div>
                <label className="cmp-gallery-dropzone">
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple onChange={handleGalleryFilesChange} disabled={isSavingPhotos} />
                  <i className="lni lni-cloud-upload"></i>
                  <strong>{isSavingPhotos ? tt("uploading_to_google_drive", "Uploading to Google Drive") : tt("choose_product_photos", "Choose photos")}</strong>
                  <span>{isSavingPhotos ? tt("product_photos_uploading_hint", "Photos are uploaded immediately") : tt("choose_product_photos_hint", "Select photos and upload starts automatically")}</span>
                </label>


                {photoUploadQueue.length > 0 && (
                  <div className="cmp-upload-progress">
                    <div className="cmp-upload-progress-head">
                      <strong>{tt("uploading_to_google_drive", "Uploading to Google Drive")}</strong>
                      <span>{productPhotoUploadProgress}%</span>
                    </div>
                    <div className="cmp-upload-progress-track"><span style={{ width: `${productPhotoUploadProgress}%` }} /></div>
                    <div className="cmp-upload-progress-list">
                      {photoUploadQueue.map((item) => (
                        <div key={item.id} className={`cmp-upload-progress-item ${item.status}`}>
                          <div>
                            <strong title={item.name}>{item.name}</strong>
                            <small>{item.status === "failed" ? item.error : `${Math.round(item.progress || 0)}%`}</small>
                          </div>
                          <span className="cmp-upload-progress-status">
                            {item.status === "done" ? "✓" : item.status === "failed" ? "!" : item.status === "uploading" ? "…" : "•"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </aside>
            </div>

            <div className="cmp-photo-manager-foot">
              <div className="cmp-photo-manager-summary">
                <i className="lni lni-information"></i>
                <span>{tt("first_photo_shop_hint", "The newest photo is used as the main image in the shop.")}</span>
              </div>
              <div className="cmp-photo-manager-actions">
                <button type="button" className="cmp-btn cmp-btn-soft" onClick={closePhotoManager}>{t("close")}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingCategory && (
        <div className="cmp-modal-backdrop">
          <div className="cmp-modal">
            <div className="cmp-modal-head">
              <h3>{t("edit_category")}</h3>
              <button type="button" onClick={() => setEditingCategory(null)}>
                ✕
              </button>
            </div>

            <div className="cmp-modal-body">
              <label>{t("category")}</label>
              <input
                type="text"
                className="cmp-input"
                value={updatedCategoryName}
                onChange={(e) => setUpdatedCategoryName(e.target.value)}
              />
              {storeCatalogEnabled && editingCategory.catalog_category_id && (
                <div className="cmp-inline-guidance">
                  <i className="lni lni-information"></i>
                  <span>{t("catalog_leaf_link_locked_hint")}</span>
                </div>
              )}
            </div>

            <div className="cmp-modal-foot">
              <button
                type="button"
                className="cmp-btn cmp-btn-soft"
                onClick={() => setEditingCategory(null)}
              >
                {t("cancel")}
              </button>
              <button
                type="button"
                className="cmp-btn cmp-btn-primary"
                onClick={handleUpdateCategory}
              >
                {t("update")}
              </button>
            </div>
          </div>
        </div>
      )}


      {fieldManagerCategory && (
        <div className="cmp-modal-backdrop">
          <div className="cmp-modal cmp-modal-large">
            <div className="cmp-modal-head">
              <h3>{tt("manage_fields", "Manage fields")} — {getCategoryName(fieldManagerCategory)}</h3>
              <button type="button" onClick={() => setFieldManagerCategory(null)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateCustomField} className="cmp-modal-body cmp-custom-fields-manager">
              <div className="cmp-modal-grid">
                <div className="cmp-field">
                  <label>{tt("field_key", "Field key")}</label>
                  <input
                    type="text"
                    className="cmp-input"
                    value={newCustomField.key}
                    onChange={(e) => setNewCustomField({ ...newCustomField, key: e.target.value })}
                    placeholder={t("category_weight_key_placeholder")}
                  />
                </div>

                <div className="cmp-field">
                  <label>{tt("field_label", "Field label")}</label>
                  <input
                    type="text"
                    className="cmp-input"
                    value={newCustomField.label}
                    onChange={(e) => setNewCustomField({ ...newCustomField, label: e.target.value })}
                    placeholder={t("category_weight_label_placeholder")}
                  />
                </div>

                <div className="cmp-field">
                  <label>{tt("field_type", "Field type")}</label>
                  <select
                    className="cmp-input cmp-select"
                    value={newCustomField.data_type}
                    onChange={(e) => setNewCustomField({ ...newCustomField, data_type: e.target.value })}
                  >
                    <option value="text">{t("field_type_text")}</option>
                    <option value="number">{t("field_type_number")}</option>
                    <option value="date">{t("date")}</option>
                    <option value="boolean">{t("field_type_boolean")}</option>
                    <option value="select">{t("select")}</option>
                  </select>
                </div>

                <label className="cmp-check-field">
                  <input
                    type="checkbox"
                    checked={newCustomField.is_visible_in_table}
                    onChange={(e) => setNewCustomField({ ...newCustomField, is_visible_in_table: e.target.checked })}
                  />
                  <span>{tt("visible_in_table", "Visible in table")}</span>
                </label>
              </div>

              <button type="submit" className="cmp-btn cmp-btn-primary cmp-custom-field-add-btn">
                + {tt("add_field", "Add field")}
              </button>

              <div className="cmp-custom-field-list">
                {subcategoryFields.length === 0 && (
                  <div className="cmp-empty">{tt("no_custom_fields", "No custom fields yet")}</div>
                )}

                {subcategoryFields.map((field) => (
                  <div className="cmp-custom-field-row" key={field.id}>
                    <div>
                      <strong>{field.label} {field?.options_json?.system_locked && <em className="cmp-system-badge">{tt("required", "Обязательное")}</em>}</strong>
                      <span>{field.key} · {field.data_type}</span>
                    </div>
                    <button
                      type="button"
                      className="cmp-icon-btn cmp-delete"
                      onClick={() => !field?.options_json?.system_locked && handleDeleteCustomField(field.id)}
                      disabled={!!field?.options_json?.system_locked}
                      title={field?.options_json?.system_locked ? tt("shop_system_field_locked", "Обязательное поле интернет-магазина") : t("delete")}
                    >
                      <i className="lni lni-trash-3"></i>
                    </button>
                  </div>
                ))}
              </div>
            </form>
          </div>
        </div>
      )}
      {contentManagerSubcategory && (
        <div className="cmp-modal-backdrop cmp-content-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) setContentManagerSubcategory(null); }}>
          <div className="cmp-modal cmp-content-modal">
            <div className="cmp-modal-head cmp-content-head">
              <div>
                <span className="cmp-content-eyebrow">{tt("online_store", "Online store")}</span>
                <h3>{tt("product_content", "Product content")}: {getSubcategoryName(contentManagerSubcategory)}</h3>
                <p>{tt("product_content_hint", "Manage the information customers see on the product page.")}</p>
              </div>
              <button type="button" className="cmp-modal-close" onClick={() => setContentManagerSubcategory(null)}>×</button>
            </div>

            {isLoadingContent ? (
              <div className="cmp-content-loading">{tt("loading", "Loading…")}</div>
            ) : (
              <div className="cmp-content-body">
                <div className="cmp-content-language-tabs">
                  {["ru", "et", "en"].map((lang) => (
                    <div key={lang} className={`cmp-language-tab-wrap ${contentLanguage === lang ? "active" : ""}`}>
                      <button type="button" className="cmp-language-main" onClick={() => setContentLanguage(lang)}>
                        {lang === "ru" ? "RU · Русский" : lang === "et" ? "ET · Eesti" : "EN · English"}
                      </button>
                      {lang !== contentLanguage && (
                        <button
                          type="button"
                          className="cmp-language-translate"
                          onClick={() => autoTranslateShopContent(lang)}
                          disabled={Boolean(shopTranslationBusy)}
                          title={tt("shop_translate_to_language", "Перевести все текстовые поля на этот язык")}
                        >
                          {shopTranslationBusy === lang ? <span className="cmp-translate-spinner" /> : <span className="cmp-translate-spark">✦</span>}
                          <span>{shopTranslationBusy === lang ? tt("translating", "Перевод…") : tt("translate", "Перевести")}</span>
                        </button>
                      )}
                    </div>
                  ))}
                  <span className="cmp-language-translate-hint">{tt("shop_translate_hint", "Кнопка ✦ переводит Description, Delivery, Payment, SEO и FAQ с открытого языка. Сам таб открывает ручное редактирование.")}</span>
                </div>

                <div className="cmp-shop-required-bar">
                  <div>
                    <span className="cmp-content-eyebrow">{tt("shop_required_data", "Обязательные данные магазина")}</span>
                    <strong>{tt("shop_required_data_hint", "Эти поля нужны для публикации товара и не могут быть удалены")}</strong>
                  </div>
                  <label className="cmp-shop-publish-toggle">
                    <input type="checkbox" checked={shopContent.shop_enabled !== false && shopContent.shop_enabled !== "false"} onChange={(e) => updateShopContent("shop_enabled", e.target.checked)} />
                    <span>{tt("shop_published", "Опубликован в магазине")}</span>
                  </label>
                </div>

                <div className="cmp-content-grid">
                  <section className="cmp-content-card cmp-content-card-wide cmp-shop-essential-card">
                    <div className="cmp-content-card-title"><span>00</span><div><h4>{tt("shop_main_data", "Основные данные магазина")}</h4><p>{tt("shop_main_data_hint", "Название, цена и остаток берутся из карточки товара. Здесь хранится витринная информация.")}</p></div></div>
                    <label>{tt("shop_sku", "SKU / Артикул")} <span className="cmp-auto-badge">AUTO</span></label>
                    <input value={shopContent.shop_sku || ""} onChange={(e) => updateShopContent("shop_sku", e.target.value)} placeholder={tt("shop_sku_auto_placeholder", "Будет создан автоматически после добавления товара")} />
                    <small className="cmp-field-help">{tt("shop_sku_hint_auto", "Оставьте пустым — система создаст уникальный SKU автоматически. При необходимости впишите свой.")}</small>
                  </section>

                  <section className="cmp-content-card cmp-content-card-wide">
                    <div className="cmp-content-card-title"><span>01</span><div><h4>{tt("product_description", "Description")}</h4><p>{tt("product_description_hint", "Clear customer-facing product information")}</p></div></div>
                    <label>{tt("short_description", "Short description")} <b className="cmp-required-star">*</b></label>
                    <textarea rows="3" value={shopContent[`shop_short_description_${contentLanguage}`] || ""} onChange={(e) => updateShopContent(`shop_short_description_${contentLanguage}`, e.target.value)} placeholder={tt("short_description_placeholder", "A short summary shown next to the price")}></textarea>
                    <label>{tt("full_description", "Full description")} <b className="cmp-required-star">*</b></label>
                    <textarea rows="8" value={shopContent[`shop_description_${contentLanguage}`] || ""} onChange={(e) => updateShopContent(`shop_description_${contentLanguage}`, e.target.value)} placeholder={tt("full_description_placeholder", "Describe benefits, use cases, materials and important details")}></textarea>
                  </section>

                  <section className="cmp-content-card">
                    <div className="cmp-content-card-title"><span>02</span><div><h4>{tt("delivery_and_payment", "Доставка и оплата")}</h4><p>{tt("delivery_payment_hint", "Используйте шаблон или сохраните свой текст для следующих товаров")}</p></div></div>
                    <label>{tt("delivery", "Доставка")}</label>
                    <div className="cmp-template-toolbar">
                      <select value={selectedShopTemplate.delivery} onChange={(e) => setSelectedShopTemplate((prev) => ({ ...prev, delivery: e.target.value }))}>
                        <option value="">{tt("choose_template", "Выберите шаблон")}</option>
                        {(shopTemplates.delivery || []).map((template) => <option key={template.id} value={template.id}>{template.builtin ? "★ " : ""}{template.name}</option>)}
                      </select>
                      <button type="button" className="cmp-template-apply" disabled={!selectedShopTemplate.delivery} onClick={() => applyContentTemplate("delivery")}>{tt("apply", "Применить")}</button>
                      <button type="button" className="cmp-template-save" disabled={shopTemplateBusy.startsWith("delivery")} onClick={() => saveContentTemplate("delivery")}>{tt("save_as_template", "Сохранить как шаблон")}</button>
                      <button type="button" className="cmp-template-delete" disabled={!selectedShopTemplate.delivery || (shopTemplates.delivery || []).find((x) => String(x.id) === String(selectedShopTemplate.delivery))?.builtin} onClick={() => deleteSelectedContentTemplate("delivery")} title={tt("delete_template", "Удалить шаблон")}>×</button>
                    </div>
                    <textarea rows="4" value={shopContent[`shop_delivery_${contentLanguage}`] || ""} onChange={(e) => updateShopContent(`shop_delivery_${contentLanguage}`, e.target.value)} placeholder={tt("delivery_template_hint", "Напишите условия доставки на выбранном языке")}></textarea>
                    <label>{tt("payment", "Оплата")}</label>
                    <div className="cmp-template-toolbar">
                      <select value={selectedShopTemplate.payment} onChange={(e) => setSelectedShopTemplate((prev) => ({ ...prev, payment: e.target.value }))}>
                        <option value="">{tt("choose_template", "Выберите шаблон")}</option>
                        {(shopTemplates.payment || []).map((template) => <option key={template.id} value={template.id}>{template.builtin ? "★ " : ""}{template.name}</option>)}
                      </select>
                      <button type="button" className="cmp-template-apply" disabled={!selectedShopTemplate.payment} onClick={() => applyContentTemplate("payment")}>{tt("apply", "Применить")}</button>
                      <button type="button" className="cmp-template-save" disabled={shopTemplateBusy.startsWith("payment")} onClick={() => saveContentTemplate("payment")}>{tt("save_as_template", "Сохранить как шаблон")}</button>
                      <button type="button" className="cmp-template-delete" disabled={!selectedShopTemplate.payment || (shopTemplates.payment || []).find((x) => String(x.id) === String(selectedShopTemplate.payment))?.builtin} onClick={() => deleteSelectedContentTemplate("payment")} title={tt("delete_template", "Удалить шаблон")}>×</button>
                    </div>
                    <textarea rows="4" value={shopContent[`shop_payment_${contentLanguage}`] || ""} onChange={(e) => updateShopContent(`shop_payment_${contentLanguage}`, e.target.value)} placeholder={tt("payment_template_hint", "Напишите условия оплаты на выбранном языке")}></textarea>
                    <div className="cmp-template-language-note">{tt("template_language_note", "Шаблон сохраняет RU / ET / EN одновременно. Если заполнен только один язык, магазин использует его и для остальных языков.")}</div>
                  </section>

                  <section className="cmp-content-card">
                    <div className="cmp-content-card-title"><span>03</span><div><h4>{tt("seo_and_video", "SEO & Video")}</h4><p>{tt("seo_video_hint", "Search preview and optional product video")}</p></div></div>
                    <label>{tt("seo_title", "SEO title")}</label>
                    <input value={shopContent[`shop_seo_title_${contentLanguage}`] || ""} onChange={(e) => updateShopContent(`shop_seo_title_${contentLanguage}`, e.target.value)} />
                    <label>{tt("seo_description", "SEO description")}</label>
                    <textarea rows="3" value={shopContent[`shop_seo_description_${contentLanguage}`] || ""} onChange={(e) => updateShopContent(`shop_seo_description_${contentLanguage}`, e.target.value)}></textarea>
                    <label>{tt("video_url", "Video URL")}</label>
                    <input value={shopContent.shop_video_url || ""} onChange={(e) => updateShopContent("shop_video_url", e.target.value)} placeholder="https://youtube.com/..." />
                  </section>

                  <section className="cmp-content-card cmp-content-card-wide">
                    <div className="cmp-content-card-title"><span>04</span><div><h4>FAQ</h4><p>{tt("faq_hint", "Добавляйте вопросы вручную или используйте готовые многоязычные шаблоны")}</p></div></div>
                    <div className="cmp-faq-template-bar">
                      <select value={selectedShopTemplate.faq} onChange={(e) => setSelectedShopTemplate((prev) => ({ ...prev, faq: e.target.value }))}>
                        <option value="">{tt("choose_faq_template", "Готовый вопрос из шаблона")}</option>
                        {(shopTemplates.faq || []).map((template) => <option key={template.id} value={template.id}>{template.builtin ? "★ " : ""}{template.name}</option>)}
                      </select>
                      <button type="button" className="cmp-template-apply" disabled={!selectedShopTemplate.faq} onClick={() => applyContentTemplate("faq")}>+ {tt("add_from_template", "Добавить из шаблона")}</button>
                      <button type="button" className="cmp-template-delete" disabled={!selectedShopTemplate.faq || (shopTemplates.faq || []).find((x) => String(x.id) === String(selectedShopTemplate.faq))?.builtin} onClick={() => deleteSelectedContentTemplate("faq")} title={tt("delete_template", "Удалить шаблон")}>×</button>
                    </div>
                    <div className="cmp-template-language-note">{tt("faq_language_note", "Вопрос и ответ хранятся на RU / ET / EN. Если перевод не добавлен, покупатель увидит заполненную версию на другом языке.")}</div>
                    <div className="cmp-faq-editor">
                      {parseFaq().map((item, index) => (
                        <div className="cmp-faq-row" key={index}>
                          <div>
                            <input value={item[`question_${contentLanguage}`] || ""} onChange={(e) => updateFaq(index, `question_${contentLanguage}`, e.target.value)} placeholder={tt("question", "Вопрос")} />
                            <textarea rows="2" value={item[`answer_${contentLanguage}`] || ""} onChange={(e) => updateFaq(index, `answer_${contentLanguage}`, e.target.value)} placeholder={tt("answer", "Ответ")}></textarea>
                            <button type="button" className="cmp-faq-save-template" disabled={shopTemplateBusy === `faq:${index}`} onClick={() => saveContentTemplate("faq", index)}>{tt("save_question_as_template", "Сохранить этот вопрос как шаблон")}</button>
                          </div>
                          <button type="button" className="cmp-faq-remove" onClick={() => removeFaq(index)} aria-label={tt("delete", "Удалить")}>×</button>
                        </div>
                      ))}
                      <button type="button" className="cmp-add-faq" onClick={addFaq}>+ {tt("add_question", "Добавить вопрос")}</button>
                    </div>
                  </section>
                </div>
              </div>
            )}

            <div className="cmp-modal-foot">
              <button type="button" className="cmp-btn cmp-btn-soft" onClick={() => setContentManagerSubcategory(null)}>{t("cancel")}</button>
              <button type="button" className="cmp-btn cmp-btn-primary" disabled={isSavingContent || isLoadingContent} onClick={saveShopContent}>{isSavingContent ? tt("saving", "Saving…") : tt("save", "Save")}</button>
            </div>
          </div>
        </div>
      )}

      {editingSubcategory && (
        <div className="cmp-modal-backdrop">
          <div className="cmp-modal cmp-modal-large">
            <div className="cmp-modal-head">
              <h3>{storeCatalogEnabled ? t("edit_catalog_item") : (t("edit_subcategory") || t("edit"))}</h3>
              <button type="button" onClick={() => setEditingSubcategory(null)}>
                ✕
              </button>
            </div>

            <div className="cmp-modal-body">
              <div className="cmp-modal-grid">
                <div className="cmp-field">
                  <label>{storeCatalogEnabled ? t("catalog_item") : t("subcategory")}</label>

                  {updatedSubcategoryName?.startsWith("preset_") ? (
                    <input
                      type="text"
                      className="cmp-input"
                      value={t(updatedSubcategoryName)}
                      readOnly
                    />
                  ) : (
                    <input
                      type="text"
                      className="cmp-input"
                      value={updatedSubcategoryName}
                      onChange={(e) => setUpdatedSubcategoryName(e.target.value)}
                    />
                  )}
                </div>

                <div className="cmp-field">
                  <label>{t("store_catalog_section")}</label>
                  <select
                    className="cmp-input cmp-select"
                    value={editingSubcategory.catalog_category_id || ""}
                    onChange={(e) => setEditingSubcategory({ ...editingSubcategory, catalog_category_id: e.target.value ? Number(e.target.value) : null })}
                  >
                    <option value="">{t("catalog_not_assigned")}</option>
                    {catalogOptions.map((node) => (
                      <option key={node.id} value={node.id}>{`${"— ".repeat(node.depth)}${node.name}`}</option>
                    ))}
                  </select>
                </div>

                <div className="cmp-field">
                  <label>{t("price_per_piece")}</label>
                  <input
                    type="number"
                    className="cmp-input"
                    value={editingSubcategory.price_per_piece || ""}
                    onChange={(e) =>
                      setEditingSubcategory({
                        ...editingSubcategory,
                        price_per_piece: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="cmp-field">
                  <label>{t("count")}</label>
                  <input
                    type="number"
                    className="cmp-input"
                    min="0"
                    value={editingSubcategory.count || ""}
                    onChange={(e) =>
                      setEditingSubcategory({
                        ...editingSubcategory,
                        count: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="cmp-field">
                  <label>{t("booked")}</label>
                  <input
                    type="number"
                    className="cmp-input"
                    value={Math.max(Number(editingSubcategory.booked || 0), 0)}
                    readOnly
                  />
                </div>

                <div className="cmp-field">
                  <label>{t("balance")}</label>
                  <input
                    type="number"
                    className="cmp-input"
                    value={getSubcategoryBalance(editingSubcategory)}
                    readOnly
                  />
                </div>

                {isEditingSubcategoryWarehouseLike && (
                  <>
                    <div className="cmp-field">
                      <label>{t("length")}</label>
                      <input
                        type="number"
                        className="cmp-input"
                        value={editingSubcategory.length || ""}
                        onChange={(e) =>
                          setEditingSubcategory({
                            ...editingSubcategory,
                            length: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="cmp-field">
                      <label>{t("width")}</label>
                      <input
                        type="number"
                        className="cmp-input"
                        value={editingSubcategory.width || ""}
                        onChange={(e) =>
                          setEditingSubcategory({
                            ...editingSubcategory,
                            width: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="cmp-field">
                      <label>{t("height")}</label>
                      <input
                        type="number"
                        className="cmp-input"
                        value={editingSubcategory.height || ""}
                        onChange={(e) =>
                          setEditingSubcategory({
                            ...editingSubcategory,
                            height: e.target.value,
                          })
                        }
                      />
                    </div>
                  </>
                )}

                {visibleSubcategoryFields.map((field) => (
                  <div className="cmp-field" key={field.id}>
                    <label>{field.label}</label>
                    {renderCustomFieldInput(
                      field,
                      editingSubcategory.custom_field_values?.[field.key],
                      (value) =>
                        updateEditingSubcategoryCustomValue(field.key, value)
                    )}
                  </div>
                ))}
              </div>

              {retailShopEnabled && (
                <div className="cmp-edit-shop-section">
                  <div className="cmp-create-section-divider"></div>
                  <div className="cmp-create-required-title">
                    <div>
                      <strong>{tt("store_product_data", "Данные интернет-магазина")}</strong>
                      <span>{tt("store_product_data_hint", "Эти данные сразу используются карточкой товара в VelShop.")}</span>
                    </div>
                    <span className="cmp-required-count">4 {tt("required", "обязательных")}</span>
                  </div>

                  {isLoadingContent ? (
                    <div className="cmp-content-loading">{tt("loading", "Загрузка…")}</div>
                  ) : (
                    <>
                      <div className="cmp-create-shop-grid">
                        <div className="cmp-create-shop-field">
                          <label>{tt("shop_sku", "SKU / Артикул")} <span className="cmp-auto-badge">AUTO</span></label>
                          <input value={shopContent.shop_sku || ""} onChange={(e) => updateShopContent("shop_sku", e.target.value)} placeholder={tt("shop_sku_auto_placeholder", "Создастся автоматически")} />
                        </div>
                        <div className="cmp-create-shop-field cmp-create-shop-field-wide">
                          <label>{tt("short_description", "Краткое описание")} <b>*</b></label>
                          <textarea rows="3" value={shopContent.shop_short_description_ru || ""} onChange={(e) => updateShopContent("shop_short_description_ru", e.target.value)} />
                        </div>
                        <div className="cmp-create-shop-field cmp-create-shop-field-wide">
                          <label>{tt("full_description", "Полное описание")} <b>*</b></label>
                          <textarea rows="6" value={shopContent.shop_description_ru || ""} onChange={(e) => updateShopContent("shop_description_ru", e.target.value)} />
                        </div>
                      </div>
                      <div className="cmp-create-publish-row">
                        <div>
                          <strong>{tt("publish_in_store", "Показывать в интернет-магазине")}</strong>
                          <span>{tt("publish_in_store_hint", "Выключите, если товар временно не должен быть виден покупателям")}</span>
                        </div>
                        <label className="cmp-shop-publish-toggle">
                          <input type="checkbox" checked={shopContent.shop_enabled !== false && shopContent.shop_enabled !== "false"} onChange={(e) => updateShopContent("shop_enabled", e.target.checked)} />
                          <span>{shopContent.shop_enabled !== false && shopContent.shop_enabled !== "false" ? tt("published", "Опубликован") : tt("draft", "Скрыт")}</span>
                        </label>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="cmp-modal-foot">
              <button
                type="button"
                className="cmp-btn cmp-btn-soft"
                onClick={() => setEditingSubcategory(null)}
              >
                {t("cancel")}
              </button>
              <button
                type="button"
                className="cmp-btn cmp-btn-primary"
                onClick={handleUpdateSubcategory}
              >
                {t("update")}
              </button>
            </div>
          </div>
        </div>
      )}

    </main>
  );
}

export default CategoryManagerPage;
